import React from "react";
import "./LessonVisual.css";
import SupportResistanceVisual from "./SupportResistanceVisual.js";

function CandlestickBasicsVisual() {
  return (
    <div className="lessonVisualCard candlestickVisual">
      <div className="lessonVisualHeader">
        <div>
          <span>CANDLESTICK VISUAL</span>
          <h3>Open, high, low, close, body, and wicks</h3>
        </div>

        <strong className="candlestickBadge">
          Price action
        </strong>
      </div>

      <div className="candlestickVisualBody">
        <div className="candlestickChartPanel">
          <span className="visualPanelLabel">
            CANDLE STRUCTURE
          </span>

          <svg
            viewBox="0 0 900 430"
            role="img"
            aria-label="Bullish and bearish candlestick structure with wick and body labels"
          >
            {/* Left candle guide lines */}
            <line
              x1="95"
              y1="70"
              x2="360"
              y2="70"
              className="candlestickGuideLine"
            />
            <line
              x1="95"
              y1="165"
              x2="360"
              y2="165"
              className="candlestickGuideLine"
            />
            <line
              x1="95"
              y1="285"
              x2="360"
              y2="285"
              className="candlestickGuideLine"
            />
            <line
              x1="95"
              y1="380"
              x2="360"
              y2="380"
              className="candlestickGuideLine"
            />

            {/* Right candle guide lines */}
            <line
              x1="520"
              y1="70"
              x2="790"
              y2="70"
              className="candlestickGuideLine"
            />
            <line
              x1="520"
              y1="165"
              x2="790"
              y2="165"
              className="candlestickGuideLine"
            />
            <line
              x1="520"
              y1="285"
              x2="790"
              y2="285"
              className="candlestickGuideLine"
            />
            <line
              x1="520"
              y1="380"
              x2="790"
              y2="380"
              className="candlestickGuideLine"
            />

            {/* Bullish candle */}
            <line
              x1="235"
              y1="70"
              x2="235"
              y2="380"
              className="bullishWick"
            />
            <rect
              x="180"
              y="165"
              width="110"
              height="120"
              rx="8"
              className="bullishBody"
            />

            {/* Bearish candle */}
            <line
              x1="635"
              y1="70"
              x2="635"
              y2="380"
              className="bearishWick"
            />
            <rect
              x="580"
              y="165"
              width="110"
              height="120"
              rx="8"
              className="bearishBody"
            />

            {/* Left labels */}
            <text x="58" y="78" className="wickTextLabel">
              High
            </text>
            <text x="34" y="173" className="closeTextLabel">
              Close
            </text>
            <text x="40" y="293" className="openTextLabel">
              Open
            </text>
            <text x="60" y="388" className="wickTextLabel">
              Low
            </text>

            {/* Right labels */}
            <text x="805" y="78" className="wickTextLabel">
              High
            </text>
            <text x="805" y="173" className="openTextLabel">
              Open
            </text>
            <text x="805" y="293" className="closeTextLabel">
              Close
            </text>
            <text x="805" y="388" className="wickTextLabel">
              Low
            </text>

            {/* Wick and body callouts */}
            <text x="126" y="118" className="wickAreaLabel">
              Upper wick
            </text>
            <line
              x1="170"
              y1="124"
              x2="225"
              y2="95"
              className="candlestickCallout"
            />

            <text x="100" y="232" className="bodyAreaLabel">
              Real body
            </text>
            <line
              x1="170"
              y1="238"
              x2="180"
              y2="225"
              className="candlestickCallout"
            />

            <text x="126" y="345" className="wickAreaLabel">
              Lower wick
            </text>
            <line
              x1="175"
              y1="334"
              x2="225"
              y2="350"
              className="candlestickCallout"
            />

            {/* Titles */}
            <text x="175" y="420" className="bullishCandleTitle">
              Bullish candle
            </text>
            <text x="565" y="420" className="bearishCandleTitle">
              Bearish candle
            </text>
          </svg>
        </div>

        <div className="candlestickMeaningPanel">
          <span>CANDLE MEANING</span>

          <div className="candlestickMeaningItem">
            <strong>1</strong>
            <div>
              <small>BODY</small>
              <h4>Open to close</h4>
              <p>
                The body shows the distance between the opening
                price and the closing price.
              </p>
            </div>
          </div>

          <div className="candlestickMeaningItem">
            <strong>2</strong>
            <div>
              <small>UPPER WICK</small>
              <h4>Highest traded price</h4>
              <p>
                The upper wick shows how far price moved above the
                candle body.
              </p>
            </div>
          </div>

          <div className="candlestickMeaningItem">
            <strong>3</strong>
            <div>
              <small>LOWER WICK</small>
              <h4>Lowest traded price</h4>
              <p>
                The lower wick shows how far price moved below the
                candle body.
              </p>
            </div>
          </div>

          <p className="candlestickMeaningNote">
            Candle meaning becomes stronger when combined with trend,
            support, resistance, volume, and confirmation.
          </p>
        </div>
      </div>

      <div className="candlestickExampleGrid">
        <div className="candlestickExampleChart">
          <span className="visualPanelLabel">
            LONG LOWER-WICK EXAMPLE
          </span>

          <svg
            viewBox="0 0 900 360"
            role="img"
            aria-label="Example of a bullish candle with a long lower wick near support"
          >
            <line
              x1="80"
              y1="255"
              x2="820"
              y2="255"
              className="supportReferenceLine"
            />

            {/* candles */}
            <line x1="180" y1="145" x2="180" y2="285" className="exampleBearWick" />
            <rect x="150" y="165" width="60" height="70" rx="6" className="exampleBearBody" />

            <line x1="300" y1="120" x2="300" y2="270" className="exampleBearWick" />
            <rect x="270" y="155" width="60" height="85" rx="6" className="exampleBearBody" />

            <line x1="430" y1="85" x2="430" y2="285" className="exampleBullWick" />
            <rect x="390" y="125" width="80" height="115" rx="6" className="exampleBullBody" />

            <line x1="565" y1="90" x2="565" y2="195" className="exampleBullWick" />
            <rect x="535" y="110" width="60" height="70" rx="6" className="exampleBullBody" />

            <line x1="690" y1="55" x2="690" y2="155" className="exampleBullWick" />
            <rect x="660" y="85" width="60" height="60" rx="6" className="exampleBullBody" />

            {/* labels */}
            <text x="90" y="238" className="supportReferenceText">
              Support $5.00
            </text>

            <text x="510" y="60" className="buyersControlText">
              Buyers regain control
            </text>

            <text x="480" y="292" className="sellerRejectionText">
              Sellers rejected below support
            </text>

            <text x="395" y="312" className="exampleWickLabel">
              Long lower wick
            </text>

            <line
              x1="455"
              y1="300"
              x2="433"
              y2="274"
              className="candlestickCallout"
            />
          </svg>
        </div>

        <div className="candlestickExampleNotes">
          <span>WHAT THIS MAY SHOW</span>

          <div>
            <strong>Long lower wick</strong>
            <p>
              Sellers pushed price lower, but buyers recovered before
              the candle closed.
            </p>
          </div>

          <div>
            <strong>Close near the high</strong>
            <p>
              Buyers controlled the final part of the candle period.
            </p>
          </div>

          <div>
            <strong>Support remains important</strong>
            <p>
              The candle is more meaningful because it formed near a
              recognized price level.
            </p>
          </div>
        </div>
      </div>



      <div className="lessonVisualExplanation">
        <div>
          <span>1</span>
          <p>The body shows the open-to-close movement.</p>
        </div>

        <div>
          <span>2</span>
          <p>The wicks show the highest and lowest prices.</p>
        </div>

        <div>
          <span>3</span>
          <p>Context determines whether the candle matters.</p>
        </div>
      </div>
    </div>
  );
}

function MacdDivergenceVisual() {
  return (
    <div className="lessonVisualCard">
      <div className="lessonVisualHeader">
        <div>
          <span>MACD VISUAL</span>
          <h3>Price rises while momentum weakens</h3>
        </div>

        <strong>Bearish divergence</strong>
      </div>

      <div className="macdVisualChart">
        <div className="macdPricePanel">
          <span className="visualPanelLabel">PRICE</span>

          <svg
            viewBox="0 0 800 240"
            role="img"
            aria-label="Price making higher highs"
          >
            <line
              x1="30"
              y1="205"
              x2="770"
              y2="205"
              className="visualAxis"
            />

            <polyline
              points="
                40,185
                120,155
                190,170
                270,120
                350,145
                440,90
                520,115
                610,55
                700,80
                760,35
              "
              className="priceVisualLine"
            />

            <line
              x1="270"
              y1="120"
              x2="760"
              y2="35"
              className="divergenceGuide priceGuide"
            />

            <circle
              cx="270"
              cy="120"
              r="7"
              className="priceVisualPoint"
            />

            <circle
              cx="760"
              cy="35"
              r="7"
              className="priceVisualPoint"
            />

            <text
              x="280"
              y="100"
              className="visualText"
            >
              Higher high
            </text>

            <text
              x="650"
              y="28"
              className="visualText"
            >
              Higher high
            </text>
          </svg>
        </div>

        <div className="macdMomentumPanel">
          <span className="visualPanelLabel">MACD</span>

          <svg
            viewBox="0 0 800 240"
            role="img"
            aria-label="MACD making lower highs"
          >
            <line
              x1="30"
              y1="120"
              x2="770"
              y2="120"
              className="visualAxis"
            />

            <polyline
              points="
                40,165
                120,110
                190,145
                270,70
                350,125
                440,85
                520,130
                610,105
                700,145
                760,125
              "
              className="macdVisualLine"
            />

            <line
              x1="270"
              y1="70"
              x2="760"
              y2="125"
              className="divergenceGuide macdGuide"
            />

            <circle
              cx="270"
              cy="70"
              r="7"
              className="macdVisualPoint"
            />

            <circle
              cx="760"
              cy="125"
              r="7"
              className="macdVisualPoint"
            />

            <g className="histogramBars">
              <rect x="60" y="120" width="28" height="62" />
              <rect x="105" y="120" width="28" height="48" />
              <rect x="150" y="120" width="28" height="38" />
              <rect x="195" y="120" width="28" height="30" />
              <rect x="240" y="120" width="28" height="23" />
              <rect x="285" y="120" width="28" height="18" />
              <rect x="330" y="120" width="28" height="14" />
              <rect x="375" y="120" width="28" height="10" />
            </g>

            <text
              x="280"
              y="52"
              className="visualText"
            >
              Stronger momentum
            </text>

            <text
              x="625"
              y="115"
              className="visualText"
            >
              Lower high
            </text>
          </svg>
        </div>
      </div>

      <div className="lessonVisualExplanation">
        <div>
          <span>1</span>
          <p>Price continues making higher highs.</p>
        </div>

        <div>
          <span>2</span>
          <p>MACD makes a lower high instead.</p>
        </div>

        <div>
          <span>3</span>
          <p>
            Momentum is weakening even though price is still
            rising.
          </p>
        </div>
      </div>
    </div>
  );
}

function StopLossVisual() {
  return (
    <div className="lessonVisualCard">
      <div className="lessonVisualHeader">
        <div>
          <span>RISK MANAGEMENT VISUAL</span>
          <h3>Entry, stop, target, and position size</h3>
        </div>

        <strong className="stopLossVisualBadge">
          Planned risk
        </strong>
      </div>

      <div className="stopLossVisualBody">
        <div className="stopLossChartPanel">
          <span className="visualPanelLabel">
            TRADE STRUCTURE
          </span>

          <svg
            viewBox="0 0 800 360"
            role="img"
            aria-label="Long trade with entry, stop, and target"
          >
            <line
              x1="60"
              y1="300"
              x2="750"
              y2="300"
              className="visualAxis"
            />

            <polyline
              points="
                70,270
                145,245
                220,255
                300,205
                380,220
                465,165
                545,180
                630,115
                720,75
              "
              className="stopLossPriceLine"
            />

            <line
              x1="60"
              y1="205"
              x2="750"
              y2="205"
              className="tradeLevelLine entryLevelLine"
            />

            <line
              x1="60"
              y1="255"
              x2="750"
              y2="255"
              className="tradeLevelLine stopLevelLine"
            />

            <line
              x1="60"
              y1="105"
              x2="750"
              y2="105"
              className="tradeLevelLine targetLevelLine"
            />

            <rect
              x="610"
              y="105"
              width="110"
              height="100"
              className="rewardZone"
            />

            <rect
              x="610"
              y="205"
              width="110"
              height="50"
              className="riskZone"
            />

            <text
              x="75"
              y="194"
              className="entryLevelText"
            >
              Entry $5.20
            </text>

            <text
              x="75"
              y="244"
              className="stopLevelText"
            >
              Stop $5.10
            </text>

            <text
              x="75"
              y="94"
              className="targetLevelText"
            >
              Target $5.40
            </text>

            <text
              x="625"
              y="155"
              className="rewardZoneText"
            >
              Reward
            </text>

            <text
              x="642"
              y="238"
              className="riskZoneText"
            >
              Risk
            </text>

            <line
              x1="565"
              y1="205"
              x2="565"
              y2="255"
              className="riskMeasurementLine"
            />

            <text
              x="480"
              y="237"
              className="visualTextSmall"
            >
              $0.10 risk/share
            </text>
          </svg>
        </div>

        <div className="stopLossCalculationPanel">
          <span>POSITION SIZE EXAMPLE</span>

          <div className="stopLossFormula">
            <div>
              <small>Maximum planned loss</small>
              <strong>$50</strong>
            </div>

            <b>÷</b>

            <div>
              <small>Risk per share</small>
              <strong>$0.10</strong>
            </div>

            <b>=</b>

            <div className="stopLossFormulaResult">
              <small>Position size</small>
              <strong>500 shares</strong>
            </div>
          </div>

          <p>
            The stop is based on the trade’s invalidation level.
            Position size is then calculated from the distance
            between the entry and stop.
          </p>
        </div>
      </div>

      <div className="lessonVisualExplanation">
        <div>
          <span>1</span>
          <p>Choose the entry only after confirmation.</p>
        </div>

        <div>
          <span>2</span>
          <p>Place the stop where the trade idea fails.</p>
        </div>

        <div>
          <span>3</span>
          <p>Calculate shares from your maximum dollar risk.</p>
        </div>
      </div>
    </div>
  );
}

function ShortSellingVisual() {
  return (
    <div className="lessonVisualCard">
      <div className="lessonVisualHeader">
        <div>
          <span>SHORT SELLING VISUAL</span>
          <h3>Borrow, sell, and buy to cover</h3>
        </div>

        <strong className="shortSellingVisualBadge">
          Advanced risk
        </strong>
      </div>

      <div className="shortSellingVisualBody">
        <div className="shortSellingProcess">
          <div className="shortSellingStep">
            <span>1</span>

            <div>
              <small>BORROW</small>
              <strong>Borrow 100 shares</strong>
              <p>
                The broker must locate shares that are available
                to borrow.
              </p>
            </div>
          </div>

          <div className="shortSellingArrow">→</div>

          <div className="shortSellingStep">
            <span>2</span>

            <div>
              <small>SELL SHORT</small>
              <strong>Sell at $10.00</strong>
              <p>
                The borrowed shares are sold into the market.
              </p>
            </div>
          </div>

          <div className="shortSellingArrow">→</div>

          <div className="shortSellingStep">
            <span>3</span>

            <div>
              <small>PRICE FALLS</small>
              <strong>Stock falls to $8.00</strong>
              <p>
                The trader benefits when the stock price declines.
              </p>
            </div>
          </div>

          <div className="shortSellingArrow">→</div>

          <div className="shortSellingStep">
            <span>4</span>

            <div>
              <small>BUY TO COVER</small>
              <strong>Repurchase at $8.00</strong>
              <p>
                Buying the shares back closes the short position.
              </p>
            </div>
          </div>
        </div>

        <div className="shortSellingResultGrid">
          <div className="shortSellingProfitCard">
            <span>PROFIT EXAMPLE</span>

            <div className="shortSellingCalculation">
              <div>
                <small>Short-sale price</small>
                <strong>$10.00</strong>
              </div>

              <b>−</b>

              <div>
                <small>Cover price</small>
                <strong>$8.00</strong>
              </div>

              <b>=</b>

              <div>
                <small>Profit per share</small>
                <strong>$2.00</strong>
              </div>
            </div>

            <p>
              With 100 shares, the gross profit is $200 before
              borrowing costs, commissions, fees, and taxes.
            </p>
          </div>

          <div className="shortSellingRiskCard">
            <span>⚠️</span>

            <div>
              <small>UNLIMITED-LOSS WARNING</small>
              <strong>Price can continue rising</strong>

              <p>
                If the stock rises from $10.00 to $14.00, the short
                position loses $4.00 per share. Unlike a long stock
                position, a short sale has no natural maximum loss.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="lessonVisualExplanation">
        <div>
          <span>1</span>
          <p>Confirm that shares are available to borrow.</p>
        </div>

        <div>
          <span>2</span>
          <p>Define the stop before entering the short.</p>
        </div>

        <div>
          <span>3</span>
          <p>Use buy to cover when closing the position.</p>
        </div>
      </div>
    </div>
  );
}

function HowStocksMoveVisual() {
  return (
    <div className="lessonVisualCard">
      <div className="lessonVisualHeader">
        <div>
          <span>PRICE MOVEMENT VISUAL</span>
          <h3>Demand, supply, volume, and liquidity</h3>
        </div>

        <strong className="marketVisualBadge">
          Market pressure
        </strong>
      </div>

      <div className="marketVisualBody">
        <div className="marketBalancePanel">
          <div className="marketSide marketBuyerSide">
            <span>BUYING PRESSURE</span>

            <strong>Price can rise</strong>

            <p>
              Aggressive buyers accept higher asking prices
              and consume the shares available for sale.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Ask</small>
                <strong>$10.00</strong>
                <span>500 shares</span>
              </div>

              <div>
                <small>Ask</small>
                <strong>$10.02</strong>
                <span>300 shares</span>
              </div>

              <div>
                <small>Ask</small>
                <strong>$10.05</strong>
                <span>200 shares</span>
              </div>
            </div>
          </div>

          <div className="marketPriceDiscovery">
            <span>PRICE MOVEMENT</span>

            <strong>$10.00 → $10.05</strong>

            <div className="marketPressureMeter">
              <div className="marketDemandPressure">
                Demand increases
              </div>

              <div className="marketSupplyPressure">
                Shares consumed
              </div>
            </div>

            <p>
              Price moves higher when buyers continue paying
              the next available asking prices.
            </p>
          </div>

          <div className="marketSide marketSellerSide">
            <span>SELLING PRESSURE</span>

            <strong>Price can fall</strong>

            <p>
              Aggressive sellers accept lower bids and supply
              more shares than buyers are willing to absorb.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Bid</small>
                <strong>$9.98</strong>
                <span>350 shares</span>
              </div>

              <div>
                <small>Bid</small>
                <strong>$9.95</strong>
                <span>250 shares</span>
              </div>

              <div>
                <small>Bid</small>
                <strong>$9.90</strong>
                <span>150 shares</span>
              </div>
            </div>
          </div>
        </div>

        <div className="marketBreakoutPanel">
          <div className="marketBreakoutChart">
            <span className="visualPanelLabel">
              NEWS, VOLUME, AND PARTICIPATION
            </span>

            <svg
              viewBox="0 0 800 320"
              role="img"
              aria-label="Stock price rising as volume and buying participation increase"
            >
              <line
                x1="60"
                y1="245"
                x2="750"
                y2="245"
                className="visualAxis"
              />

              <polyline
                points="
                  70,225
                  145,215
                  220,220
                  295,190
                  365,175
                  435,140
                  505,115
                  575,90
                  645,60
                  720,35
                "
                className="marketBreakoutPriceLine"
              />

              <g className="marketVolumeBars">
                <rect x="90" y="265" width="34" height="22" />
                <rect x="145" y="258" width="34" height="29" />
                <rect x="200" y="250" width="34" height="37" />
                <rect x="255" y="240" width="34" height="47" />
                <rect x="310" y="225" width="34" height="62" />
                <rect x="365" y="210" width="34" height="77" />
                <rect x="420" y="190" width="34" height="97" />
                <rect x="475" y="170" width="34" height="117" />
                <rect x="530" y="145" width="34" height="142" />
                <rect x="585" y="125" width="34" height="162" />
              </g>

              <text
                x="80"
                y="205"
                className="marketResistanceText"
              >
                News released
              </text>

              <text
                x="530"
                y="75"
                className="marketBreakoutText"
              >
                Buyers become aggressive
              </text>

              <text
                x="500"
                y="305"
                className="marketVolumeText"
              >
                Volume increases
              </text>
            </svg>
          </div>

          <div className="marketConfirmationCard">
            <span>WHAT STRENGTHENS A MOVE?</span>

            <div className="marketConfirmationItem">
              <strong>1</strong>

              <div>
                <small>Catalyst</small>
                <p>
                  Meaningful news gives traders a reason to act.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>2</strong>

              <div>
                <small>Volume</small>
                <p>
                  More participation can support the movement.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>3</strong>

              <div>
                <small>Liquidity</small>
                <p>
                  Fewer available shares can create faster moves
                  and greater risk.
                </p>
              </div>
            </div>

            <p className="marketConfirmationNote">
              A fast price move is not automatically a safe entry.
              Traders still need confirmation and risk control.
            </p>
          </div>
        </div>
      </div>

      <div className="lessonVisualExplanation">
        <div>
          <span>1</span>
          <p>Aggressive buyers can move price higher.</p>
        </div>

        <div>
          <span>2</span>
          <p>Aggressive sellers can move price lower.</p>
        </div>

        <div>
          <span>3</span>
          <p>Volume and liquidity affect the speed of movement.</p>
        </div>
      </div>
    </div>
  );
}

function MarketOrdersVisual() {
  return (
    <div className="lessonVisualCard">
      <div className="lessonVisualHeader">
        <div>
          <span>ORDER TYPE VISUAL</span>
          <h3>Market orders versus limit orders</h3>
        </div>

        <strong className="marketVisualBadge">
          Execution control
        </strong>
      </div>

      <div className="marketVisualBody">
        <div className="marketBalancePanel">
          <div className="marketSide marketBuyerSide">
            <span>MARKET ORDER</span>

            <strong>Speed first</strong>

            <p>
              The order attempts to execute immediately at the best
              prices currently available.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Ask</small>
                <strong>$5.05</strong>
                <span>200 shares</span>
              </div>

              <div>
                <small>Ask</small>
                <strong>$5.07</strong>
                <span>300 shares</span>
              </div>

              <div>
                <small>Ask</small>
                <strong>$5.10</strong>
                <span>500 shares</span>
              </div>
            </div>
          </div>

          <div className="marketPriceDiscovery">
            <span>EXECUTION EXAMPLE</span>

            <strong>Buy 500 shares</strong>

            <div className="marketPressureMeter">
              <div className="marketDemandPressure">
                200 at $5.05
              </div>

              <div className="marketSupplyPressure">
                300 at $5.07
              </div>
            </div>

            <p>
              A market order may fill across more than one price
              when there are not enough shares at the best ask.
            </p>
          </div>

          <div className="marketSide marketSellerSide">
            <span>LIMIT ORDER</span>

            <strong>Price control first</strong>

            <p>
              A buy limit at $5.05 prevents paying more than $5.05,
              but the full order may not execute.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Limit</small>
                <strong>$5.05</strong>
                <span>Maximum price</span>
              </div>

              <div>
                <small>Filled</small>
                <strong>200</strong>
                <span>shares</span>
              </div>

              <div>
                <small>Waiting</small>
                <strong>300</strong>
                <span>shares</span>
              </div>
            </div>
          </div>
        </div>

        <div className="marketBreakoutPanel">
          <div className="marketBreakoutChart">
            <span className="visualPanelLabel">
              SPEED VERSUS PRICE CONTROL
            </span>

            <svg
              viewBox="0 0 800 320"
              role="img"
              aria-label="Comparison of market order execution and limit order price control"
            >
              <line
                x1="70"
                y1="245"
                x2="730"
                y2="245"
                className="visualAxis"
              />

              <line
                x1="100"
                y1="210"
                x2="700"
                y2="210"
                className="marketResistanceLine"
              />

              <polyline
                points="
                  90,225
                  180,215
                  270,200
                  360,175
                  450,145
                  540,105
                  630,75
                  710,45
                "
                className="marketBreakoutPriceLine"
              />

              <text
                x="95"
                y="198"
                className="marketResistanceText"
              >
                Limit price $5.05
              </text>

              <text
                x="470"
                y="95"
                className="marketBreakoutText"
              >
                Market moves higher
              </text>

              <text
                x="430"
                y="300"
                className="marketVolumeText"
              >
                Limit order may remain unfilled
              </text>
            </svg>
          </div>

          <div className="marketConfirmationCard">
            <span>ORDER TYPE CHECKLIST</span>

            <div className="marketConfirmationItem">
              <strong>1</strong>

              <div>
                <small>Spread</small>
                <p>
                  A wider spread increases execution uncertainty.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>2</strong>

              <div>
                <small>Liquidity</small>
                <p>
                  Fewer available shares can increase slippage.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>3</strong>

              <div>
                <small>Urgency</small>
                <p>
                  Decide whether speed or price control matters more.
                </p>
              </div>
            </div>

            <p className="marketConfirmationNote">
              A displayed quote is not a guaranteed execution price.
            </p>
          </div>
        </div>
      </div>

      <div className="lessonVisualExplanation">
        <div>
          <span>1</span>
          <p>Market orders prioritize immediate execution.</p>
        </div>

        <div>
          <span>2</span>
          <p>Limit orders prioritize price control.</p>
        </div>

        <div>
          <span>3</span>
          <p>Liquidity and spread affect the final result.</p>
        </div>
      </div>
    </div>
  );
}

function BidAskSpreadVisual() {
  return (
    <div className="lessonVisualCard">
      <div className="lessonVisualHeader">
        <div>
          <span>BID, ASK, AND SPREAD VISUAL</span>
          <h3>How buyers and sellers meet in the market</h3>
        </div>

        <strong className="marketVisualBadge">
          Execution quality
        </strong>
      </div>

      <div className="marketVisualBody">
        <div className="marketBalancePanel">
          <div className="marketSide marketBuyerSide">
            <span>BID</span>

            <strong>Highest buyer price</strong>

            <p>
              The bid shows the highest displayed price a buyer is
              currently willing to pay.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Bid</small>
                <strong>$5.00</strong>
                <span>600 shares</span>
              </div>

              <div>
                <small>Bid</small>
                <strong>$4.98</strong>
                <span>900 shares</span>
              </div>

              <div>
                <small>Bid</small>
                <strong>$4.95</strong>
                <span>1,200 shares</span>
              </div>
            </div>
          </div>

          <div className="marketPriceDiscovery">
            <span>SPREAD</span>

            <strong>$0.08</strong>

            <div className="marketPressureMeter">
              <div className="marketDemandPressure">
                Bid $5.00
              </div>

              <div className="marketSupplyPressure">
                Ask $5.08
              </div>
            </div>

            <p>
              The spread is the difference between the best bid and
              the best ask.
            </p>
          </div>

          <div className="marketSide marketSellerSide">
            <span>ASK</span>

            <strong>Lowest seller price</strong>

            <p>
              The ask shows the lowest displayed price a seller is
              currently willing to accept.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Ask</small>
                <strong>$5.08</strong>
                <span>300 shares</span>
              </div>

              <div>
                <small>Ask</small>
                <strong>$5.10</strong>
                <span>500 shares</span>
              </div>

              <div>
                <small>Ask</small>
                <strong>$5.15</strong>
                <span>800 shares</span>
              </div>
            </div>
          </div>
        </div>

        <div className="marketBreakoutPanel">
          <div className="marketBreakoutChart">
            <span className="visualPanelLabel">
              WIDE SPREAD EXAMPLE
            </span>

            <svg
              viewBox="0 0 800 320"
              role="img"
              aria-label="Bid at five dollars, ask at five dollars and eight cents, creating an eight-cent spread"
            >
              <line
                x1="70"
                y1="245"
                x2="730"
                y2="245"
                className="visualAxis"
              />

              <line
                x1="120"
                y1="205"
                x2="680"
                y2="205"
                className="marketResistanceLine"
              />

              <line
                x1="120"
                y1="110"
                x2="680"
                y2="110"
                className="marketBreakoutPriceLine"
              />

              <text
                x="135"
                y="195"
                className="marketResistanceText"
              >
                Best bid $5.00
              </text>

              <text
                x="135"
                y="100"
                className="marketBreakoutText"
              >
                Best ask $5.08
              </text>

              <text
                x="460"
                y="165"
                className="marketVolumeText"
              >
                $0.08 spread
              </text>

              <line
                x1="610"
                y1="110"
                x2="610"
                y2="205"
                className="riskMeasurementLine"
              />
            </svg>
          </div>

          <div className="marketConfirmationCard">
            <span>SPREAD CHECKLIST</span>

            <div className="marketConfirmationItem">
              <strong>1</strong>

              <div>
                <small>Spread size</small>
                <p>
                  A wider spread increases immediate execution cost.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>2</strong>

              <div>
                <small>Liquidity</small>
                <p>
                  Low liquidity can make the spread wider and less stable.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>3</strong>

              <div>
                <small>Order size</small>
                <p>
                  Larger orders may fill across multiple price levels.
                </p>
              </div>
            </div>

            <p className="marketConfirmationNote">
              Buying at the ask and immediately selling at the bid can
              create an instant loss equal to the spread.
            </p>
          </div>
        </div>
      </div>

      <div className="lessonVisualExplanation">
        <div>
          <span>1</span>
          <p>The bid is the highest displayed buying price.</p>
        </div>

        <div>
          <span>2</span>
          <p>The ask is the lowest displayed selling price.</p>
        </div>

        <div>
          <span>3</span>
          <p>The spread affects execution cost and trade risk.</p>
        </div>
      </div>
    </div>
  );
}

function VolumeLiquidityVisual() {
  return (
    <div className="lessonVisualCard">
      <div className="lessonVisualHeader">
        <div>
          <span>VOLUME AND LIQUIDITY VISUAL</span>
          <h3>Participation, execution, and price movement</h3>
        </div>

        <strong className="marketVisualBadge">
          Market participation
        </strong>
      </div>

      <div className="marketVisualBody">
        <div className="marketBalancePanel">
          <div className="marketSide marketBuyerSide">
            <span>HIGH LIQUIDITY</span>

            <strong>Easier execution</strong>

            <p>
              More available shares and active participation can
              create tighter spreads and more stable fills.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Bid</small>
                <strong>$10.00</strong>
                <span>2,500 shares</span>
              </div>

              <div>
                <small>Ask</small>
                <strong>$10.01</strong>
                <span>2,200 shares</span>
              </div>

              <div>
                <small>Spread</small>
                <strong>$0.01</strong>
                <span>Tight</span>
              </div>
            </div>
          </div>

          <div className="marketPriceDiscovery">
            <span>ORDER EXAMPLE</span>

            <strong>Buy 1,000 shares</strong>

            <div className="marketPressureMeter">
              <div className="marketDemandPressure">
                Strong liquidity
              </div>

              <div className="marketSupplyPressure">
                Stable execution
              </div>
            </div>

            <p>
              A liquid stock may fill most or all of the order near
              the expected price.
            </p>
          </div>

          <div className="marketSide marketSellerSide">
            <span>LOW LIQUIDITY</span>

            <strong>Greater execution risk</strong>

            <p>
              Fewer available shares can lead to wider spreads,
              partial fills, and greater slippage.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Ask</small>
                <strong>$10.05</strong>
                <span>200 shares</span>
              </div>

              <div>
                <small>Ask</small>
                <strong>$10.10</strong>
                <span>300 shares</span>
              </div>

              <div>
                <small>Ask</small>
                <strong>$10.18</strong>
                <span>500 shares</span>
              </div>
            </div>
          </div>
        </div>

        <div className="marketBreakoutPanel">
          <div className="marketBreakoutChart">
            <span className="visualPanelLabel">
              VOLUME CONFIRMATION
            </span>

            <svg
              viewBox="0 0 800 320"
              role="img"
              aria-label="Price breakout supported by increasing trading volume"
            >
              <line
                x1="60"
                y1="235"
                x2="750"
                y2="235"
                className="visualAxis"
              />

              <line
                x1="60"
                y1="145"
                x2="750"
                y2="145"
                className="marketResistanceLine"
              />

              <polyline
                points="
                  70,220
                  145,205
                  220,215
                  295,185
                  365,195
                  435,155
                  500,165
                  565,135
                  635,90
                  720,55
                "
                className="marketBreakoutPriceLine"
              />

              <g className="marketVolumeBars">
                <rect x="90" y="260" width="34" height="25" />
                <rect x="145" y="255" width="34" height="30" />
                <rect x="200" y="250" width="34" height="35" />
                <rect x="255" y="245" width="34" height="40" />
                <rect x="310" y="238" width="34" height="47" />
                <rect x="365" y="228" width="34" height="57" />
                <rect x="420" y="215" width="34" height="70" />
                <rect x="475" y="195" width="34" height="90" />
                <rect x="530" y="170" width="34" height="115" />
                <rect x="585" y="140" width="34" height="145" />
              </g>

              <text
                x="75"
                y="135"
                className="marketResistanceText"
              >
                Resistance
              </text>

              <text
                x="560"
                y="78"
                className="marketBreakoutText"
              >
                Breakout
              </text>

              <text
                x="500"
                y="305"
                className="marketVolumeText"
              >
                Volume expands
              </text>
            </svg>
          </div>

          <div className="marketConfirmationCard">
            <span>LIQUIDITY CHECKLIST</span>

            <div className="marketConfirmationItem">
              <strong>1</strong>

              <div>
                <small>Volume</small>
                <p>
                  More trading activity may improve participation.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>2</strong>

              <div>
                <small>Spread</small>
                <p>
                  Tighter spreads usually improve execution quality.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>3</strong>

              <div>
                <small>Available shares</small>
                <p>
                  Thin order books can increase slippage and partial fills.
                </p>
              </div>
            </div>

            <p className="marketConfirmationNote">
              High volume does not always guarantee high liquidity.
              Spread and available share size must also be checked.
            </p>
          </div>
        </div>
      </div>

      <div className="lessonVisualExplanation">
        <div>
          <span>1</span>
          <p>Volume measures how many shares are traded.</p>
        </div>

        <div>
          <span>2</span>
          <p>Liquidity measures how easily orders can execute.</p>
        </div>

        <div>
          <span>3</span>
          <p>Low liquidity increases spread and slippage risk.</p>
        </div>
      </div>
    </div>
  );
}

function RelativeVolumeVisual() {
  return (
    <div className="lessonVisualCard">
      <div className="lessonVisualHeader">
        <div>
          <span>RELATIVE VOLUME VISUAL</span>
          <h3>Current activity compared with normal activity</h3>
        </div>

        <strong className="marketVisualBadge">
          Participation strength
        </strong>
      </div>

      <div className="marketVisualBody">
        <div className="marketBalancePanel">
          <div className="marketSide marketBuyerSide">
            <span>NORMAL ACTIVITY</span>

            <strong>Relative volume 1.0</strong>

            <p>
              Current trading activity is close to the stock’s
              typical activity for the same time of day.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Typical volume</small>
                <strong>500,000</strong>
                <span>shares by 10:00 AM</span>
              </div>

              <div>
                <small>Current volume</small>
                <strong>500,000</strong>
                <span>shares</span>
              </div>

              <div>
                <small>RVOL</small>
                <strong>1.0</strong>
                <span>Normal</span>
              </div>
            </div>
          </div>

          <div className="marketPriceDiscovery">
            <span>RELATIVE VOLUME</span>

            <strong>Current ÷ Typical</strong>

            <div className="marketPressureMeter">
              <div className="marketDemandPressure">
                Current activity
              </div>

              <div className="marketSupplyPressure">
                Normal activity
              </div>
            </div>

            <p>
              Relative volume compares current participation with
              what is normally expected at the same time.
            </p>
          </div>

          <div className="marketSide marketSellerSide">
            <span>UNUSUAL ACTIVITY</span>

            <strong>Relative volume 3.0</strong>

            <p>
              Current trading activity is three times stronger than
              normal, showing unusually high participation.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Typical volume</small>
                <strong>500,000</strong>
                <span>shares by 10:00 AM</span>
              </div>

              <div>
                <small>Current volume</small>
                <strong>1,500,000</strong>
                <span>shares</span>
              </div>

              <div>
                <small>RVOL</small>
                <strong>3.0</strong>
                <span>Unusual</span>
              </div>
            </div>
          </div>
        </div>

        <div className="marketBreakoutPanel">
          <div className="marketBreakoutChart">
            <span className="visualPanelLabel">
              RELATIVE VOLUME COMPARISON
            </span>

            <svg
              viewBox="0 0 800 320"
              role="img"
              aria-label="Comparison between normal volume and three-times-normal relative volume"
            >
              <line
                x1="70"
                y1="250"
                x2="730"
                y2="250"
                className="visualAxis"
              />

              <rect
                x="180"
                y="175"
                width="120"
                height="75"
                className="marketVolumeBars"
              />

              <rect
                x="500"
                y="55"
                width="120"
                height="195"
                className="marketVolumeBars"
              />

              <text
                x="190"
                y="165"
                className="marketResistanceText"
              >
                RVOL 1.0
              </text>

              <text
                x="510"
                y="45"
                className="marketBreakoutText"
              >
                RVOL 3.0
              </text>

              <text
                x="170"
                y="285"
                className="marketVolumeText"
              >
                Normal activity
              </text>

              <text
                x="480"
                y="285"
                className="marketVolumeText"
              >
                Unusual activity
              </text>
            </svg>
          </div>

          <div className="marketConfirmationCard">
            <span>RVOL CHECKLIST</span>

            <div className="marketConfirmationItem">
              <strong>1</strong>

              <div>
                <small>Time comparison</small>
                <p>
                  Compare volume with the same time of day, not only
                  the full-day average.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>2</strong>

              <div>
                <small>Direction</small>
                <p>
                  High relative volume can support strong buying or
                  strong selling.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>3</strong>

              <div>
                <small>Context</small>
                <p>
                  Combine RVOL with catalyst, price action, liquidity,
                  and nearby levels.
                </p>
              </div>
            </div>

            <p className="marketConfirmationNote">
              High relative volume confirms unusual participation,
              not a guaranteed bullish move.
            </p>
          </div>
        </div>
      </div>

      <div className="lessonVisualExplanation">
        <div>
          <span>1</span>
          <p>RVOL 1.0 means activity is close to normal.</p>
        </div>

        <div>
          <span>2</span>
          <p>RVOL above 1.0 means activity is stronger than usual.</p>
        </div>

        <div>
          <span>3</span>
          <p>High RVOL does not determine price direction by itself.</p>
        </div>
      </div>
    </div>
  );
}

function FloatShareSupplyVisual() {
  return (
    <div className="lessonVisualCard">
      <div className="lessonVisualHeader">
        <div>
          <span>FLOAT AND SHARE SUPPLY VISUAL</span>
          <h3>Why limited tradable supply can increase volatility</h3>
        </div>

        <strong className="marketVisualBadge">
          Supply risk
        </strong>
      </div>

      <div className="marketVisualBody">
        <div className="marketBalancePanel">
          <div className="marketSide marketBuyerSide">
            <span>HIGHER FLOAT</span>

            <strong>More shares available</strong>

            <p>
              A larger tradable supply may absorb buying and selling
              pressure more easily.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Public float</small>
                <strong>200M</strong>
                <span>shares</span>
              </div>

              <div>
                <small>Current volume</small>
                <strong>5M</strong>
                <span>shares</span>
              </div>

              <div>
                <small>Price behavior</small>
                <strong>Slower</strong>
                <span>movement</span>
              </div>
            </div>
          </div>

          <div className="marketPriceDiscovery">
            <span>SHARE SUPPLY</span>

            <strong>Demand versus float</strong>

            <div className="marketPressureMeter">
              <div className="marketDemandPressure">
                Buying demand
              </div>

              <div className="marketSupplyPressure">
                Tradable shares
              </div>
            </div>

            <p>
              Price can move faster when strong demand competes for
              a smaller supply of available shares.
            </p>
          </div>

          <div className="marketSide marketSellerSide">
            <span>LOW FLOAT</span>

            <strong>Fewer shares available</strong>

            <p>
              Limited tradable supply can increase volatility,
              slippage, spreads, and halt risk.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Public float</small>
                <strong>8M</strong>
                <span>shares</span>
              </div>

              <div>
                <small>Current volume</small>
                <strong>20M</strong>
                <span>shares</span>
              </div>

              <div>
                <small>Price behavior</small>
                <strong>Faster</strong>
                <span>movement</span>
              </div>
            </div>
          </div>
        </div>

        <div className="marketBreakoutPanel">
          <div className="marketBreakoutChart">
            <span className="visualPanelLabel">
              LIMITED SUPPLY EXAMPLE
            </span>

            <svg
              viewBox="0 0 800 320"
              role="img"
              aria-label="Low-float stock rising quickly as volume exceeds available share supply"
            >
              <line
                x1="60"
                y1="245"
                x2="740"
                y2="245"
                className="visualAxis"
              />

              <polyline
                points="
                  70,225
                  150,210
                  230,190
                  310,165
                  390,135
                  470,95
                  560,55
                  700,30
                "
                className="marketBreakoutPriceLine"
              />

              <g className="marketVolumeBars">
                <rect x="100" y="265" width="34" height="20" />
                <rect x="160" y="255" width="34" height="30" />
                <rect x="220" y="245" width="34" height="40" />
                <rect x="280" y="230" width="34" height="55" />
                <rect x="340" y="210" width="34" height="75" />
                <rect x="400" y="185" width="34" height="100" />
                <rect x="460" y="155" width="34" height="130" />
                <rect x="520" y="120" width="34" height="165" />
              </g>

              <text
                x="90"
                y="205"
                className="marketResistanceText"
              >
                Float 8M shares
              </text>

              <text
                x="500"
                y="70"
                className="marketBreakoutText"
              >
                Rapid price expansion
              </text>

              <text
                x="470"
                y="305"
                className="marketVolumeText"
              >
                Volume 20M shares
              </text>
            </svg>
          </div>

          <div className="marketConfirmationCard">
            <span>LOW-FLOAT CHECKLIST</span>

            <div className="marketConfirmationItem">
              <strong>1</strong>

              <div>
                <small>Float</small>
                <p>
                  Confirm how many shares are generally tradable.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>2</strong>

              <div>
                <small>Relative volume</small>
                <p>
                  Compare current activity with normal activity.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>3</strong>

              <div>
                <small>Risk</small>
                <p>
                  Reduce size when volatility, spread, or halt risk is high.
                </p>
              </div>
            </div>

            <p className="marketConfirmationNote">
              Low float can create opportunity, but it also increases
              execution and loss risk.
            </p>
          </div>
        </div>
      </div>

      <div className="lessonVisualExplanation">
        <div>
          <span>1</span>
          <p>Float measures shares generally available for trading.</p>
        </div>

        <div>
          <span>2</span>
          <p>Lower float can make price move more quickly.</p>
        </div>

        <div>
          <span>3</span>
          <p>Volume, liquidity, and risk still must be checked.</p>
        </div>
      </div>
    </div>
  );
}

function VolatilityPriceRangeVisual() {
  return (
    <div className="lessonVisualCard">
      <div className="lessonVisualHeader">
        <div>
          <span>VOLATILITY AND PRICE RANGE VISUAL</span>
          <h3>How movement speed changes trade risk</h3>
        </div>

        <strong className="marketVisualBadge">
          Movement risk
        </strong>
      </div>

      <div className="marketVisualBody">
        <div className="marketBalancePanel">
          <div className="marketSide marketBuyerSide">
            <span>LOWER VOLATILITY</span>

            <strong>Smaller price swings</strong>

            <p>
              A less volatile stock usually moves more gradually and
              may require a smaller stop distance.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Five-minute high</small>
                <strong>$10.10</strong>
                <span>price</span>
              </div>

              <div>
                <small>Five-minute low</small>
                <strong>$10.00</strong>
                <span>price</span>
              </div>

              <div>
                <small>Range</small>
                <strong>$0.10</strong>
                <span>smaller movement</span>
              </div>
            </div>
          </div>

          <div className="marketPriceDiscovery">
            <span>PRICE RANGE</span>

            <strong>High − Low</strong>

            <div className="marketPressureMeter">
              <div className="marketDemandPressure">
                Movement size
              </div>

              <div className="marketSupplyPressure">
                Risk per share
              </div>
            </div>

            <p>
              Wider price ranges usually create more risk per share
              and may require a smaller position.
            </p>
          </div>

          <div className="marketSide marketSellerSide">
            <span>HIGHER VOLATILITY</span>

            <strong>Larger price swings</strong>

            <p>
              A more volatile stock can move rapidly in both directions,
              increasing execution and stop-loss risk.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Five-minute high</small>
                <strong>$10.50</strong>
                <span>price</span>
              </div>

              <div>
                <small>Five-minute low</small>
                <strong>$10.00</strong>
                <span>price</span>
              </div>

              <div>
                <small>Range</small>
                <strong>$0.50</strong>
                <span>larger movement</span>
              </div>
            </div>
          </div>
        </div>

        <div className="marketBreakoutPanel">
          <div className="marketBreakoutChart">
            <span className="visualPanelLabel">
              RANGE COMPARISON
            </span>

            <svg
              viewBox="0 0 800 320"
              role="img"
              aria-label="Comparison between a low-volatility price range and a high-volatility price range"
            >
              <line
                x1="60"
                y1="250"
                x2="740"
                y2="250"
                className="visualAxis"
              />

              <polyline
                points="
                  80,200
                  145,185
                  210,195
                  275,175
                  340,190
                "
                className="marketResistanceLine"
              />

              <polyline
                points="
                  430,220
                  500,145
                  565,205
                  630,90
                  710,45
                "
                className="marketBreakoutPriceLine"
              />

              <text
                x="120"
                y="150"
                className="marketResistanceText"
              >
                $0.10 range
              </text>

              <text
                x="555"
                y="75"
                className="marketBreakoutText"
              >
                $0.50 range
              </text>

              <text
                x="105"
                y="285"
                className="marketVolumeText"
              >
                Lower volatility
              </text>

              <text
                x="530"
                y="285"
                className="marketVolumeText"
              >
                Higher volatility
              </text>
            </svg>
          </div>

          <div className="marketConfirmationCard">
            <span>VOLATILITY CHECKLIST</span>

            <div className="marketConfirmationItem">
              <strong>1</strong>

              <div>
                <small>Recent range</small>
                <p>
                  Measure how far price has been moving during recent
                  candles.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>2</strong>

              <div>
                <small>Stop distance</small>
                <p>
                  Place the stop beyond the setup’s invalidation level,
                  not at a random distance.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>3</strong>

              <div>
                <small>Position size</small>
                <p>
                  Reduce shares when the risk per share becomes larger.
                </p>
              </div>
            </div>

            <p className="marketConfirmationNote">
              Higher volatility can create greater opportunity, but it
              also increases slippage, stop, and loss risk.
            </p>
          </div>
        </div>
      </div>

      <div className="lessonVisualExplanation">
        <div>
          <span>1</span>
          <p>Price range equals the high minus the low.</p>
        </div>

        <div>
          <span>2</span>
          <p>Wider ranges usually mean greater volatility.</p>
        </div>

        <div>
          <span>3</span>
          <p>Greater volatility often requires fewer shares.</p>
        </div>
      </div>
    </div>
  );
}

function AverageTrueRangeVisual() {
  return (
    <div className="lessonVisualCard">
      <div className="lessonVisualHeader">
        <div>
          <span>AVERAGE TRUE RANGE VISUAL</span>
          <h3>Typical movement, stops, and position size</h3>
        </div>

        <strong className="marketVisualBadge">
          Volatility planning
        </strong>
      </div>

      <div className="marketVisualBody">
        <div className="marketBalancePanel">
          <div className="marketSide marketBuyerSide">
            <span>LOWER ATR</span>

            <strong>Smaller typical movement</strong>

            <p>
              A lower ATR means the stock has recently been moving
              within a smaller average range.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>ATR</small>
                <strong>$0.12</strong>
                <span>typical movement</span>
              </div>

              <div>
                <small>Example stop</small>
                <strong>$0.10</strong>
                <span>structure-based</span>
              </div>

              <div>
                <small>Position size</small>
                <strong>500</strong>
                <span>shares</span>
              </div>
            </div>
          </div>

          <div className="marketPriceDiscovery">
            <span>ATR PURPOSE</span>

            <strong>Measure movement</strong>

            <div className="marketPressureMeter">
              <div className="marketDemandPressure">
                Typical range
              </div>

              <div className="marketSupplyPressure">
                Risk planning
              </div>
            </div>

            <p>
              ATR helps estimate normal movement, but it does not
              predict whether price will rise or fall.
            </p>
          </div>

          <div className="marketSide marketSellerSide">
            <span>HIGHER ATR</span>

            <strong>Larger typical movement</strong>

            <p>
              A higher ATR means the stock has recently been moving
              through a wider average range.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>ATR</small>
                <strong>$0.60</strong>
                <span>typical movement</span>
              </div>

              <div>
                <small>Example stop</small>
                <strong>$0.40</strong>
                <span>structure-based</span>
              </div>

              <div>
                <small>Position size</small>
                <strong>125</strong>
                <span>shares</span>
              </div>
            </div>
          </div>
        </div>

        <div className="marketBreakoutPanel">
          <div className="marketBreakoutChart">
            <span className="visualPanelLabel">
              ATR COMPARISON
            </span>

            <svg
              viewBox="0 0 800 320"
              role="img"
              aria-label="Comparison between a stock with low ATR and a stock with high ATR"
            >
              <line
                x1="60"
                y1="250"
                x2="740"
                y2="250"
                className="visualAxis"
              />

              <polyline
                points="
                  80,205
                  145,190
                  210,198
                  275,180
                  340,192
                "
                className="marketResistanceLine"
              />

              <polyline
                points="
                  430,225
                  500,150
                  565,210
                  630,95
                  710,50
                "
                className="marketBreakoutPriceLine"
              />

              <text
                x="120"
                y="155"
                className="marketResistanceText"
              >
                ATR $0.12
              </text>

              <text
                x="555"
                y="80"
                className="marketBreakoutText"
              >
                ATR $0.60
              </text>

              <text
                x="100"
                y="285"
                className="marketVolumeText"
              >
                Smaller movement
              </text>

              <text
                x="525"
                y="285"
                className="marketVolumeText"
              >
                Larger movement
              </text>
            </svg>
          </div>

          <div className="marketConfirmationCard">
            <span>ATR CHECKLIST</span>

            <div className="marketConfirmationItem">
              <strong>1</strong>

              <div>
                <small>Movement</small>
                <p>
                  Compare current ATR with recent price behavior.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>2</strong>

              <div>
                <small>Stop placement</small>
                <p>
                  Use market structure first, then confirm the stop
                  is realistic for the stock’s ATR.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>3</strong>

              <div>
                <small>Position size</small>
                <p>
                  Reduce shares when ATR increases the risk per share.
                </p>
              </div>
            </div>

            <p className="marketConfirmationNote">
              ATR supports risk planning, but it should never replace
              the setup’s true invalidation level.
            </p>
          </div>
        </div>
      </div>

      <div className="lessonVisualExplanation">
        <div>
          <span>1</span>
          <p>ATR measures typical price movement.</p>
        </div>

        <div>
          <span>2</span>
          <p>ATR does not predict price direction.</p>
        </div>

        <div>
          <span>3</span>
          <p>Higher ATR often requires fewer shares.</p>
        </div>
      </div>
    </div>
  );
}

function RiskRewardRatioVisual() {
  return (
    <div className="lessonVisualCard">
      <div className="lessonVisualHeader">
        <div>
          <span>RISK-TO-REWARD VISUAL</span>
          <h3>Comparing planned loss with potential profit</h3>
        </div>

        <strong className="marketVisualBadge">
          Setup quality
        </strong>
      </div>

      <div className="marketVisualBody">
        <div className="marketBalancePanel">
          <div className="marketSide marketBuyerSide">
            <span>PLANNED RISK</span>

            <strong>$0.10 per share</strong>

            <p>
              Risk is the distance between the planned entry and the
              stop-loss level.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Entry</small>
                <strong>$5.20</strong>
                <span>planned price</span>
              </div>

              <div>
                <small>Stop</small>
                <strong>$5.10</strong>
                <span>invalidation level</span>
              </div>

              <div>
                <small>Risk</small>
                <strong>$0.10</strong>
                <span>per share</span>
              </div>
            </div>
          </div>

          <div className="marketPriceDiscovery">
            <span>RISK-TO-REWARD</span>

            <strong>1 : 2</strong>

            <div className="marketPressureMeter">
              <div className="marketDemandPressure">
                Risk $0.10
              </div>

              <div className="marketSupplyPressure">
                Reward $0.20
              </div>
            </div>

            <p>
              The trader is risking one unit to pursue two units of
              potential reward.
            </p>
          </div>

          <div className="marketSide marketSellerSide">
            <span>POTENTIAL REWARD</span>

            <strong>$0.20 per share</strong>

            <p>
              Reward is the distance between the planned entry and
              the target price.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Entry</small>
                <strong>$5.20</strong>
                <span>planned price</span>
              </div>

              <div>
                <small>Target</small>
                <strong>$5.40</strong>
                <span>planned exit</span>
              </div>

              <div>
                <small>Reward</small>
                <strong>$0.20</strong>
                <span>per share</span>
              </div>
            </div>
          </div>
        </div>

        <div className="marketBreakoutPanel">
          <div className="marketBreakoutChart">
            <span className="visualPanelLabel">
              ENTRY, STOP, AND TARGET
            </span>

            <svg
              viewBox="0 0 800 320"
              role="img"
              aria-label="Long trade showing entry, stop, target, risk, and reward"
            >
              <line
                x1="60"
                y1="250"
                x2="740"
                y2="250"
                className="visualAxis"
              />

              <line
                x1="80"
                y1="190"
                x2="720"
                y2="190"
                className="marketResistanceLine"
              />

              <line
                x1="80"
                y1="235"
                x2="720"
                y2="235"
                className="riskMeasurementLine"
              />

              <line
                x1="80"
                y1="95"
                x2="720"
                y2="95"
                className="marketBreakoutPriceLine"
              />

              <text
                x="95"
                y="180"
                className="marketResistanceText"
              >
                Entry $5.20
              </text>

              <text
                x="95"
                y="225"
                className="marketVolumeText"
              >
                Stop $5.10
              </text>

              <text
                x="95"
                y="85"
                className="marketBreakoutText"
              >
                Target $5.40
              </text>

              <text
                x="560"
                y="220"
                className="marketVolumeText"
              >
                Risk $0.10
              </text>

              <text
                x="545"
                y="145"
                className="marketBreakoutText"
              >
                Reward $0.20
              </text>
            </svg>
          </div>

          <div className="marketConfirmationCard">
            <span>SETUP CHECKLIST</span>

            <div className="marketConfirmationItem">
              <strong>1</strong>

              <div>
                <small>Stop validity</small>
                <p>
                  The stop must be placed where the trade idea fails.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>2</strong>

              <div>
                <small>Target realism</small>
                <p>
                  Confirm that support or resistance does not block the target.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>3</strong>

              <div>
                <small>Execution quality</small>
                <p>
                  Spread, liquidity, and slippage can change the real ratio.
                </p>
              </div>
            </div>

            <p className="marketConfirmationNote">
              A favorable ratio does not make a weak setup safe.
              Confirmation and execution conditions still matter.
            </p>
          </div>
        </div>
      </div>

      <div className="lessonVisualExplanation">
        <div>
          <span>1</span>
          <p>Risk is measured from entry to stop.</p>
        </div>

        <div>
          <span>2</span>
          <p>Reward is measured from entry to target.</p>
        </div>

        <div>
          <span>3</span>
          <p>A 1-to-2 ratio risks one unit to pursue two.</p>
        </div>
      </div>
    </div>
  );
}

function PositionSizingVisual() {
  return (
    <div className="lessonVisualCard">
      <div className="lessonVisualHeader">
        <div>
          <span>POSITION SIZING VISUAL</span>
          <h3>Matching share size to planned dollar risk</h3>
        </div>

        <strong className="marketVisualBadge">
          Capital protection
        </strong>
      </div>

      <div className="marketVisualBody">
        <div className="marketBalancePanel">
          <div className="marketSide marketBuyerSide">
            <span>MAXIMUM DOLLAR RISK</span>

            <strong>$50 planned loss</strong>

            <p>
              The trader first decides the maximum amount they are
              willing to lose if the stop is reached.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Account risk</small>
                <strong>$50</strong>
                <span>maximum loss</span>
              </div>

              <div>
                <small>Entry</small>
                <strong>$5.20</strong>
                <span>planned price</span>
              </div>

              <div>
                <small>Stop</small>
                <strong>$5.10</strong>
                <span>invalidation level</span>
              </div>
            </div>
          </div>

          <div className="marketPriceDiscovery">
            <span>POSITION SIZE FORMULA</span>

            <strong>$50 ÷ $0.10</strong>

            <div className="marketPressureMeter">
              <div className="marketDemandPressure">
                Maximum risk
              </div>

              <div className="marketSupplyPressure">
                Risk per share
              </div>
            </div>

            <p>
              Maximum dollar risk divided by risk per share equals
              the allowed position size.
            </p>
          </div>

          <div className="marketSide marketSellerSide">
            <span>CALCULATED POSITION</span>

            <strong>500 shares</strong>

            <p>
              With $0.10 of risk per share, 500 shares creates a
              planned maximum loss of $50.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Risk per share</small>
                <strong>$0.10</strong>
                <span>entry minus stop</span>
              </div>

              <div>
                <small>Shares</small>
                <strong>500</strong>
                <span>calculated size</span>
              </div>

              <div>
                <small>Total position</small>
                <strong>$2,600</strong>
                <span>at $5.20</span>
              </div>
            </div>
          </div>
        </div>

        <div className="marketBreakoutPanel">
          <div className="marketBreakoutChart">
            <span className="visualPanelLabel">
              STOP DISTANCE CHANGES SHARE SIZE
            </span>

            <svg
              viewBox="0 0 800 320"
              role="img"
              aria-label="Comparison showing that wider stop distances require fewer shares"
            >
              <line
                x1="60"
                y1="250"
                x2="740"
                y2="250"
                className="visualAxis"
              />

              <rect
                x="140"
                y="80"
                width="150"
                height="170"
                className="marketVolumeBars"
              />

              <rect
                x="510"
                y="165"
                width="150"
                height="85"
                className="marketVolumeBars"
              />

              <text
                x="160"
                y="65"
                className="marketBreakoutText"
              >
                500 shares
              </text>

              <text
                x="530"
                y="150"
                className="marketResistanceText"
              >
                250 shares
              </text>

              <text
                x="140"
                y="285"
                className="marketVolumeText"
              >
                $0.10 stop distance
              </text>

              <text
                x="500"
                y="285"
                className="marketVolumeText"
              >
                $0.20 stop distance
              </text>
            </svg>
          </div>

          <div className="marketConfirmationCard">
            <span>POSITION SIZE CHECKLIST</span>

            <div className="marketConfirmationItem">
              <strong>1</strong>

              <div>
                <small>Maximum risk</small>
                <p>
                  Set the maximum dollar loss before entering.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>2</strong>

              <div>
                <small>Stop distance</small>
                <p>
                  Measure the distance between entry and invalidation.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>3</strong>

              <div>
                <small>Execution risk</small>
                <p>
                  Reduce size when spread, slippage, or volatility is high.
                </p>
              </div>
            </div>

            <p className="marketConfirmationNote">
              Position size must be based on risk, not on how many
              shares the trader wants to buy.
            </p>
          </div>
        </div>
      </div>

      <div className="lessonVisualExplanation">
        <div>
          <span>1</span>
          <p>Choose the maximum dollar risk first.</p>
        </div>

        <div>
          <span>2</span>
          <p>Calculate risk per share from entry and stop.</p>
        </div>

        <div>
          <span>3</span>
          <p>Wider stops require fewer shares.</p>
        </div>
      </div>
    </div>
  );
}

function TradePlanningVisual() {
  return (
    <div className="lessonVisualCard">
      <div className="lessonVisualHeader">
        <div>
          <span>TRADE PLANNING VISUAL</span>
          <h3>Build the plan before entering the trade</h3>
        </div>

        <strong className="marketVisualBadge">
          Pre-trade discipline
        </strong>
      </div>

      <div className="marketVisualBody">
        <div className="marketBalancePanel">
          <div className="marketSide marketBuyerSide">
            <span>SETUP AND CONFIRMATION</span>

            <strong>Why the trade exists</strong>

            <p>
              The trader defines the pattern, catalyst, market
              condition, and confirmation needed before entry.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Setup</small>
                <strong>Breakout</strong>
                <span>above resistance</span>
              </div>

              <div>
                <small>Confirmation</small>
                <strong>Hold $5.20</strong>
                <span>with strong volume</span>
              </div>

              <div>
                <small>Catalyst</small>
                <strong>Positive news</strong>
                <span>supports interest</span>
              </div>
            </div>
          </div>

          <div className="marketPriceDiscovery">
            <span>TRADE PLAN</span>

            <strong>Complete before entry</strong>

            <div className="marketPressureMeter">
              <div className="marketDemandPressure">
                Setup quality
              </div>

              <div className="marketSupplyPressure">
                Risk control
              </div>
            </div>

            <p>
              A complete plan defines what must happen, where the
              trade fails, and how profits may be managed.
            </p>
          </div>

          <div className="marketSide marketSellerSide">
            <span>RISK AND EXIT PLAN</span>

            <strong>Know the failure point</strong>

            <p>
              Entry, stop, targets, position size, and invalidation
              should all be defined before the order is placed.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Entry</small>
                <strong>$5.20</strong>
                <span>after confirmation</span>
              </div>

              <div>
                <small>Stop</small>
                <strong>$5.10</strong>
                <span>trade invalidation</span>
              </div>

              <div>
                <small>Targets</small>
                <strong>$5.40 / $5.60</strong>
                <span>planned exits</span>
              </div>
            </div>
          </div>
        </div>

        <div className="marketBreakoutPanel">
          <div className="marketBreakoutChart">
            <span className="visualPanelLabel">
              PLANNED BREAKOUT TRADE
            </span>

            <svg
              viewBox="0 0 800 320"
              role="img"
              aria-label="Trade plan showing resistance, confirmation entry, stop, and profit targets"
            >
              <line
                x1="60"
                y1="250"
                x2="740"
                y2="250"
                className="visualAxis"
              />

              <line
                x1="70"
                y1="185"
                x2="730"
                y2="185"
                className="marketResistanceLine"
              />

              <line
                x1="70"
                y1="225"
                x2="730"
                y2="225"
                className="riskMeasurementLine"
              />

              <line
                x1="70"
                y1="115"
                x2="730"
                y2="115"
                className="marketBreakoutPriceLine"
              />

              <line
                x1="70"
                y1="70"
                x2="730"
                y2="70"
                className="marketBreakoutPriceLine"
              />

              <polyline
                points="
                  80,230
                  160,215
                  240,205
                  320,190
                  400,175
                  480,145
                  560,110
                  650,75
                  720,55
                "
                className="marketBreakoutPriceLine"
              />

              <text
                x="90"
                y="175"
                className="marketResistanceText"
              >
                Entry / resistance $5.20
              </text>

              <text
                x="90"
                y="215"
                className="marketVolumeText"
              >
                Stop $5.10
              </text>

              <text
                x="90"
                y="105"
                className="marketBreakoutText"
              >
                Target 1 $5.40
              </text>

              <text
                x="90"
                y="60"
                className="marketBreakoutText"
              >
                Target 2 $5.60
              </text>
            </svg>
          </div>

          <div className="marketConfirmationCard">
            <span>TRADE PLAN CHECKLIST</span>

            <div className="marketConfirmationItem">
              <strong>1</strong>

              <div>
                <small>Setup</small>
                <p>
                  Define the pattern, catalyst, and market condition.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>2</strong>

              <div>
                <small>Risk</small>
                <p>
                  Calculate stop distance, dollar risk, and position size.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>3</strong>

              <div>
                <small>Execution</small>
                <p>
                  Confirm spread, liquidity, entry conditions, and targets.
                </p>
              </div>
            </div>

            <p className="marketConfirmationNote">
              When important parts are missing, the trade plan is
              incomplete and the entry should wait.
            </p>
          </div>
        </div>
      </div>

      <div className="lessonVisualExplanation">
        <div>
          <span>1</span>
          <p>Define the setup and confirmation before entry.</p>
        </div>

        <div>
          <span>2</span>
          <p>Calculate the stop, risk, and position size in advance.</p>
        </div>

        <div>
          <span>3</span>
          <p>Plan both failure and profit-taking exits.</p>
        </div>
      </div>
    </div>
  );
}

function TradingPsychologyDisciplineVisual() {
  return (
    <div className="lessonVisualCard">
      <div className="lessonVisualHeader">
        <div>
          <span>TRADING PSYCHOLOGY VISUAL</span>
          <h3>Follow the plan instead of the emotion</h3>
        </div>

        <strong className="marketVisualBadge">
          Discipline under pressure
        </strong>
      </div>

      <div className="marketVisualBody">
        <div className="marketBalancePanel">
          <div className="marketSide marketBuyerSide">
            <span>DISCIPLINED RESPONSE</span>

            <strong>Plan before action</strong>

            <p>
              A disciplined trader uses a written setup, entry, stop,
              target, and position-size plan before entering.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Setup</small>
                <strong>Qualified</strong>
                <span>rules completed</span>
              </div>

              <div>
                <small>Risk</small>
                <strong>Defined</strong>
                <span>normal position size</span>
              </div>

              <div>
                <small>Decision</small>
                <strong>Controlled</strong>
                <span>plan guides action</span>
              </div>
            </div>
          </div>

          <div className="marketPriceDiscovery">
            <span>MENTAL CHECKPOINT</span>

            <strong>Pause → Review → Decide</strong>

            <div className="marketPressureMeter">
              <div className="marketDemandPressure">
                Follow the process
              </div>

              <div className="marketSupplyPressure">
                Resist emotional pressure
              </div>
            </div>

            <p>
              A pause creates space between an emotional reaction and the
              next trading decision.
            </p>
          </div>

          <div className="marketSide marketSellerSide">
            <span>EMOTIONAL RESPONSE</span>

            <strong>Impulse replaces the plan</strong>

            <p>
              Fear, greed, frustration, and excitement can lead to chasing,
              oversized positions, revenge trades, and broken stops.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Emotion</small>
                <strong>Frustration</strong>
                <span>after a loss</span>
              </div>

              <div>
                <small>Behavior</small>
                <strong>Revenge trade</strong>
                <span>low-quality setup</span>
              </div>

              <div>
                <small>Risk</small>
                <strong>Increased</strong>
                <span>rules ignored</span>
              </div>
            </div>
          </div>
        </div>

        <div className="marketBreakoutPanel">
          <div className="marketBreakoutChart">
            <span className="visualPanelLabel">
              DISCIPLINED PROCESS VS EMOTIONAL CYCLE
            </span>

            <svg
              viewBox="0 0 800 320"
              role="img"
              aria-label="Comparison between a disciplined trading process and an emotional revenge-trading cycle"
            >
              <line
                x1="60"
                y1="250"
                x2="740"
                y2="250"
                className="visualAxis"
              />

              <polyline
                points="
                  80,215
                  170,190
                  260,170
                  350,145
                  440,125
                  530,100
                  620,85
                  720,65
                "
                className="marketBreakoutPriceLine"
              />

              <polyline
                points="
                  80,210
                  170,180
                  260,220
                  350,165
                  440,235
                  530,180
                  620,245
                  720,215
                "
                className="marketResistanceLine"
              />

              <circle
                cx="260"
                cy="220"
                r="8"
                className="marketBreakoutPoint"
              />

              <circle
                cx="440"
                cy="235"
                r="8"
                className="marketBreakoutPoint"
              />

              <circle
                cx="620"
                cy="245"
                r="8"
                className="marketBreakoutPoint"
              />

              <text
                x="470"
                y="90"
                className="marketBreakoutText"
              >
                Consistent process
              </text>

              <text
                x="420"
                y="275"
                className="marketVolumeText"
              >
                Emotional decision cycle
              </text>

              <text
                x="95"
                y="195"
                className="marketResistanceText"
              >
                Loss occurs
              </text>

              <text
                x="250"
                y="245"
                className="marketVolumeText"
              >
                Pause and review
              </text>
            </svg>
          </div>

          <div className="marketConfirmationCard">
            <span>DISCIPLINE CHECKLIST</span>

            <div className="marketConfirmationItem">
              <strong>1</strong>

              <div>
                <small>Before the trade</small>
                <p>
                  Define setup, confirmation, size, stop, and targets.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>2</strong>

              <div>
                <small>After a loss</small>
                <p>
                  Pause, record what happened, and reset emotionally.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>3</strong>

              <div>
                <small>Safety limits</small>
                <p>
                  Respect consecutive-loss and daily-loss limits.
                </p>
              </div>
            </div>

            <p className="marketConfirmationNote">
              The goal is not to remove emotion. The goal is to prevent
              emotion from controlling execution.
            </p>
          </div>
        </div>
      </div>

      <div className="lessonVisualExplanation">
        <div>
          <span>1</span>
          <p>Discipline begins with a complete written plan.</p>
        </div>

        <div>
          <span>2</span>
          <p>A pause helps prevent revenge trading after a loss.</p>
        </div>

        <div>
          <span>3</span>
          <p>Daily limits protect both capital and decision quality.</p>
        </div>
      </div>
    </div>
  );
}

function Level2BasicsVisual() {
  return (
    <div className="lessonVisualCard">
      <div className="lessonVisualHeader">
        <div>
          <span>LEVEL 2 BASICS VISUAL</span>
          <h3>Read bids, asks, depth, and order pressure</h3>
        </div>

        <strong className="marketVisualBadge">
          Market depth
        </strong>
      </div>

      <div className="marketVisualBody">
        <div className="marketBalancePanel">
          <div className="marketSide marketBuyerSide">
            <span>BID SIDE</span>

            <strong>Displayed buying interest</strong>

            <p>
              Bids show prices where market participants are currently
              willing to buy shares.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Bid price</small>
                <strong>$5.18</strong>
                <span>1,200 shares</span>
              </div>

              <div>
                <small>Next bid</small>
                <strong>$5.17</strong>
                <span>2,500 shares</span>
              </div>

              <div>
                <small>Depth</small>
                <strong>Moderate</strong>
                <span>buyers below price</span>
              </div>
            </div>
          </div>

          <div className="marketPriceDiscovery">
            <span>BEST BID AND ASK</span>

            <strong>$5.18 × $5.20</strong>

            <div className="marketPressureMeter">
              <div className="marketDemandPressure">
                Buyers lifting asks
              </div>

              <div className="marketSupplyPressure">
                Sellers holding offers
              </div>
            </div>

            <p>
              The spread, changing order size, and actual executions help
              show whether buyers or sellers are gaining control.
            </p>
          </div>

          <div className="marketSide marketSellerSide">
            <span>ASK SIDE</span>

            <strong>Displayed selling interest</strong>

            <p>
              Asks show prices where market participants are currently
              willing to sell shares.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Ask price</small>
                <strong>$5.20</strong>
                <span>3,000 shares</span>
              </div>

              <div>
                <small>Next ask</small>
                <strong>$5.21</strong>
                <span>1,800 shares</span>
              </div>

              <div>
                <small>Depth</small>
                <strong>Heavy</strong>
                <span>sellers above price</span>
              </div>
            </div>
          </div>
        </div>

        <div className="marketBreakoutPanel">
          <div className="marketBreakoutChart">
            <span className="visualPanelLabel">
              LEVEL 2 ORDER BOOK
            </span>

            <svg
              viewBox="0 0 800 320"
              role="img"
              aria-label="Level 2 order book showing bid prices, ask prices, order sizes, and the spread"
            >
              <line
                x1="400"
                y1="40"
                x2="400"
                y2="285"
                className="visualAxis"
              />

              <text
                x="170"
                y="45"
                className="marketBreakoutText"
              >
                BIDS
              </text>

              <text
                x="590"
                y="45"
                className="marketResistanceText"
              >
                ASKS
              </text>

              <rect
                x="80"
                y="75"
                width="260"
                height="42"
                className="marketBreakoutPoint"
              />

              <rect
                x="105"
                y="130"
                width="235"
                height="42"
                className="marketBreakoutPoint"
              />

              <rect
                x="145"
                y="185"
                width="195"
                height="42"
                className="marketBreakoutPoint"
              />

              <rect
                x="460"
                y="75"
                width="260"
                height="42"
                className="marketResistanceLine"
              />

              <rect
                x="460"
                y="130"
                width="220"
                height="42"
                className="marketResistanceLine"
              />

              <rect
                x="460"
                y="185"
                width="180"
                height="42"
                className="marketResistanceLine"
              />

              <text
                x="105"
                y="103"
                className="marketBreakoutText"
              >
                $5.18 — 1,200
              </text>

              <text
                x="130"
                y="158"
                className="marketBreakoutText"
              >
                $5.17 — 2,500
              </text>

              <text
                x="170"
                y="213"
                className="marketBreakoutText"
              >
                $5.16 — 1,100
              </text>

              <text
                x="485"
                y="103"
                className="marketResistanceText"
              >
                $5.20 — 3,000
              </text>

              <text
                x="485"
                y="158"
                className="marketResistanceText"
              >
                $5.21 — 1,800
              </text>

              <text
                x="485"
                y="213"
                className="marketResistanceText"
              >
                $5.22 — 900
              </text>

              <text
                x="335"
                y="275"
                className="marketVolumeText"
              >
                Spread: $0.02
              </text>
            </svg>
          </div>

          <div className="marketConfirmationCard">
            <span>LEVEL 2 CHECKLIST</span>

            <div className="marketConfirmationItem">
              <strong>1</strong>

              <div>
                <small>Spread</small>
                <p>
                  Check the distance between the best bid and best ask.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>2</strong>

              <div>
                <small>Order changes</small>
                <p>
                  Watch whether size is added, removed, refreshed, or executed.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>3</strong>

              <div>
                <small>Price response</small>
                <p>
                  Confirm whether price advances, stalls, or rejects the level.
                </p>
              </div>
            </div>

            <p className="marketConfirmationNote">
              Displayed orders can disappear. Combine Level 2 with actual
              executions, price action, volume, and risk control.
            </p>
          </div>
        </div>
      </div>

      <div className="lessonVisualExplanation">
        <div>
          <span>1</span>
          <p>Bids show displayed buying interest below or at price.</p>
        </div>

        <div>
          <span>2</span>
          <p>Asks show displayed selling interest above or at price.</p>
        </div>

        <div>
          <span>3</span>
          <p>Changing orders and executions matter more than one large size.</p>
        </div>
      </div>
    </div>
  );
}

function VwapBasicsVisual() {
  return (
    <div className="lessonVisualCard">
      <div className="lessonVisualHeader">
        <div>
          <span>VWAP BASICS VISUAL</span>
          <h3>Use VWAP to evaluate intraday trade location</h3>
        </div>

        <strong className="marketVisualBadge">
          Price + volume context
        </strong>
      </div>

      <div className="marketVisualBody">
        <div className="marketBalancePanel">
          <div className="marketSide marketBuyerSide">
            <span>ABOVE VWAP</span>

            <strong>Stronger intraday context</strong>

            <p>
              Price holding above a rising VWAP may show stronger demand,
              especially when pullbacks continue finding support.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Price</small>
                <strong>$5.42</strong>
                <span>above VWAP</span>
              </div>

              <div>
                <small>VWAP</small>
                <strong>$5.30</strong>
                <span>rising</span>
              </div>

              <div>
                <small>Context</small>
                <strong>Stronger</strong>
                <span>buyers maintaining control</span>
              </div>
            </div>
          </div>

          <div className="marketPriceDiscovery">
            <span>VWAP REACTION</span>

            <strong>Hold or reject</strong>

            <div className="marketPressureMeter">
              <div className="marketDemandPressure">
                Pullback holds VWAP
              </div>

              <div className="marketSupplyPressure">
                Rally rejects at VWAP
              </div>
            </div>

            <p>
              VWAP provides context, but price action and volume must still
              confirm whether the level is acting as support or resistance.
            </p>
          </div>

          <div className="marketSide marketSellerSide">
            <span>BELOW VWAP</span>

            <strong>Weaker intraday context</strong>

            <p>
              Price remaining below a falling VWAP may show weaker demand,
              especially when rallies repeatedly fail near the line.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Price</small>
                <strong>$5.18</strong>
                <span>below VWAP</span>
              </div>

              <div>
                <small>VWAP</small>
                <strong>$5.30</strong>
                <span>falling</span>
              </div>

              <div>
                <small>Context</small>
                <strong>Weaker</strong>
                <span>sellers maintaining pressure</span>
              </div>
            </div>
          </div>
        </div>

        <div className="marketBreakoutPanel">
          <div className="marketBreakoutChart">
            <span className="visualPanelLabel">
              PRICE REACTION AROUND VWAP
            </span>

            <svg
              viewBox="0 0 800 320"
              role="img"
              aria-label="Price trades above a rising VWAP, pulls back to the line, holds, and continues higher"
            >
              <line
                x1="60"
                y1="250"
                x2="740"
                y2="250"
                className="visualAxis"
              />

              <polyline
                points="
                  80,215
                  150,205
                  220,195
                  290,180
                  360,165
                  430,150
                  500,135
                  570,120
                  640,105
                  720,90
                "
                className="marketResistanceLine"
              />

              <polyline
                points="
                  80,230
                  145,205
                  210,170
                  275,145
                  340,165
                  405,140
                  470,115
                  535,130
                  605,90
                  675,65
                  730,50
                "
                className="marketBreakoutPriceLine"
              />

              <circle
                cx="340"
                cy="165"
                r="8"
                className="marketBreakoutPoint"
              />

              <circle
                cx="535"
                cy="130"
                r="8"
                className="marketBreakoutPoint"
              />

              <text
                x="95"
                y="195"
                className="marketResistanceText"
              >
                Rising VWAP
              </text>

              <text
                x="300"
                y="190"
                className="marketBreakoutText"
              >
                Pullback holds
              </text>

              <text
                x="555"
                y="105"
                className="marketBreakoutText"
              >
                Momentum resumes
              </text>
            </svg>
          </div>

          <div className="marketConfirmationCard">
            <span>VWAP CHECKLIST</span>

            <div className="marketConfirmationItem">
              <strong>1</strong>

              <div>
                <small>Price location</small>
                <p>
                  Determine whether price is above or below VWAP.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>2</strong>

              <div>
                <small>VWAP direction</small>
                <p>
                  Check whether the line is rising, flat, or falling.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>3</strong>

              <div>
                <small>Reaction quality</small>
                <p>
                  Confirm holds or rejections with price action and volume.
                </p>
              </div>
            </div>

            <p className="marketConfirmationNote">
              VWAP supports the trade thesis, but it should never replace
              confirmation, stop placement, and risk management.
            </p>
          </div>
        </div>
      </div>

      <div className="lessonVisualExplanation">
        <div>
          <span>1</span>
          <p>Price above VWAP may support a stronger intraday bias.</p>
        </div>

        <div>
          <span>2</span>
          <p>Price below VWAP may support a weaker intraday bias.</p>
        </div>

        <div>
          <span>3</span>
          <p>Always confirm VWAP reactions with price and volume.</p>
        </div>
      </div>
    </div>
  );
}

function BreakoutsFalseBreakoutsVisual() {
  return (
    <div className="lessonVisualCard">
      <div className="lessonVisualHeader">
        <div>
          <span>BREAKOUTS VISUAL</span>
          <h3>Separate strong breakouts from failed moves</h3>
        </div>

        <strong className="marketVisualBadge">
          Hold or reject
        </strong>
      </div>

      <div className="marketVisualBody">
        <div className="marketBalancePanel">
          <div className="marketSide marketBuyerSide">
            <span>VALID BREAKOUT</span>

            <strong>Price holds above resistance</strong>

            <p>
              A stronger breakout shows acceptance above the level,
              participation, and continued buying pressure.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Resistance</small>
                <strong>$5.20</strong>
                <span>cleared</span>
              </div>

              <div>
                <small>Retest</small>
                <strong>$5.20 holds</strong>
                <span>buyers defend</span>
              </div>

              <div>
                <small>Follow-through</small>
                <strong>Present</strong>
                <span>price continues</span>
              </div>
            </div>
          </div>

          <div className="marketPriceDiscovery">
            <span>KEY TEST</span>

            <strong>Break → Hold → Continue</strong>

            <div className="marketPressureMeter">
              <div className="marketDemandPressure">
                Buyers accept higher prices
              </div>

              <div className="marketSupplyPressure">
                Sellers reject the move
              </div>
            </div>

            <p>
              The reaction after price crosses the level helps determine
              whether the breakout is real or failing.
            </p>
          </div>

          <div className="marketSide marketSellerSide">
            <span>FALSE BREAKOUT</span>

            <strong>Price falls back below the level</strong>

            <p>
              A failed breakout often shows weak participation, quick
              rejection, and trapped late buyers.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Breakout attempt</small>
                <strong>$5.24</strong>
                <span>brief move above</span>
              </div>

              <div>
                <small>Volume</small>
                <strong>Weak</strong>
                <span>limited support</span>
              </div>

              <div>
                <small>Failure</small>
                <strong>Below $5.20</strong>
                <span>level not held</span>
              </div>
            </div>
          </div>
        </div>

        <div className="marketBreakoutPanel">
          <div className="marketBreakoutChart">
            <span className="visualPanelLabel">
              VALID BREAKOUT VS FALSE BREAKOUT
            </span>

            <svg
              viewBox="0 0 800 320"
              role="img"
              aria-label="Comparison between a valid breakout that holds above resistance and a false breakout that falls back below the level"
            >
              <line
                x1="60"
                y1="250"
                x2="740"
                y2="250"
                className="visualAxis"
              />

              <line
                x1="70"
                y1="175"
                x2="730"
                y2="175"
                className="marketResistanceLine"
              />

              <polyline
                points="
                  80,225
                  135,210
                  190,195
                  245,180
                  300,150
                  355,165
                  410,145
                  470,120
                  530,95
                "
                className="marketBreakoutPriceLine"
              />

              <polyline
                points="
                  80,230
                  135,215
                  190,200
                  245,185
                  300,155
                  350,145
                  400,180
                  455,210
                  520,235
                "
                className="marketResistanceLine"
              />

              <text
                x="85"
                y="165"
                className="marketResistanceText"
              >
                Resistance $5.20
              </text>

              <text
                x="390"
                y="115"
                className="marketBreakoutText"
              >
                Valid breakout
              </text>

              <text
                x="390"
                y="235"
                className="marketVolumeText"
              >
                False breakout
              </text>

              <circle
                cx="355"
                cy="165"
                r="8"
                className="marketBreakoutPoint"
              />

              <circle
                cx="400"
                cy="180"
                r="8"
                className="marketBreakoutPoint"
              />
            </svg>
          </div>

          <div className="marketConfirmationCard">
            <span>BREAKOUT CHECKLIST</span>

            <div className="marketConfirmationItem">
              <strong>1</strong>

              <div>
                <small>Level quality</small>
                <p>
                  Confirm that support or resistance is clearly defined.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>2</strong>

              <div>
                <small>Volume and liquidity</small>
                <p>
                  Look for participation, manageable spread, and follow-through.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>3</strong>

              <div>
                <small>Hold or rejection</small>
                <p>
                  Watch whether price accepts the level or quickly loses it.
                </p>
              </div>
            </div>

            <p className="marketConfirmationNote">
              A brief move above resistance is not enough. The level must
              hold and receive continued support.
            </p>
          </div>
        </div>
      </div>

      <div className="lessonVisualExplanation">
        <div>
          <span>1</span>
          <p>A valid breakout shows acceptance above the level.</p>
        </div>

        <div>
          <span>2</span>
          <p>Volume and follow-through strengthen confirmation.</p>
        </div>

        <div>
          <span>3</span>
          <p>A quick reversal below the level warns of failure.</p>
        </div>
      </div>
    </div>
  );
}

function TrailingStopsVisual() {
  return (
    <div className="lessonVisualCard">
      <div className="lessonVisualHeader">
        <div>
          <span>TRAILING STOPS VISUAL</span>
          <h3>Protect profit while giving the trade room</h3>
        </div>

        <strong className="marketVisualBadge">
          Dynamic risk control
        </strong>
      </div>

      <div className="marketVisualBody">
        <div className="marketBalancePanel">
          <div className="marketSide marketBuyerSide">
            <span>ORIGINAL PLAN</span>

            <strong>Initial risk defined</strong>

            <p>
              The original stop protects the trade when the setup has not
              yet produced meaningful profit.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Entry</small>
                <strong>$5.20</strong>
                <span>planned execution</span>
              </div>

              <div>
                <small>Original stop</small>
                <strong>$5.10</strong>
                <span>setup invalidation</span>
              </div>

              <div>
                <small>Initial risk</small>
                <strong>$0.10</strong>
                <span>defined before entry</span>
              </div>
            </div>
          </div>

          <div className="marketPriceDiscovery">
            <span>TRAILING PROCESS</span>

            <strong>Advance → Hold → Raise</strong>

            <div className="marketPressureMeter">
              <div className="marketDemandPressure">
                Protect open profit
              </div>

              <div className="marketSupplyPressure">
                Allow normal pullbacks
              </div>
            </div>

            <p>
              The stop should move only after price creates a stronger
              support reference, such as a higher low.
            </p>
          </div>

          <div className="marketSide marketSellerSide">
            <span>UPDATED STOP</span>

            <strong>Profit protection</strong>

            <p>
              After the first target and a higher low, the stop can be
              raised without placing it too close to current price.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>First target</small>
                <strong>$5.40</strong>
                <span>partial profit taken</span>
              </div>

              <div>
                <small>Higher low</small>
                <strong>$5.32</strong>
                <span>new support area</span>
              </div>

              <div>
                <small>New stop</small>
                <strong>$5.29</strong>
                <span>below market structure</span>
              </div>
            </div>
          </div>
        </div>

        <div className="marketBreakoutPanel">
          <div className="marketBreakoutChart">
            <span className="visualPanelLabel">
              PRICE ADVANCE AND TRAILING STOP
            </span>

            <svg
              viewBox="0 0 800 320"
              role="img"
              aria-label="Price rises, forms higher lows, and the stop moves upward beneath market structure"
            >
              <line
                x1="60"
                y1="250"
                x2="740"
                y2="250"
                className="visualAxis"
              />

              <polyline
                points="
                  80,230
                  145,210
                  210,185
                  275,200
                  340,165
                  405,140
                  470,160
                  535,120
                  605,95
                  675,110
                  730,70
                "
                className="marketBreakoutPriceLine"
              />

              <polyline
                points="
                  80,275
                  210,275
                  210,230
                  405,230
                  405,190
                  605,190
                  605,145
                  730,145
                "
                className="marketResistanceLine"
              />

              <circle
                cx="275"
                cy="200"
                r="8"
                className="marketBreakoutPoint"
              />

              <circle
                cx="470"
                cy="160"
                r="8"
                className="marketBreakoutPoint"
              />

              <circle
                cx="675"
                cy="110"
                r="8"
                className="marketBreakoutPoint"
              />

              <text
                x="85"
                y="295"
                className="marketVolumeText"
              >
                Original stop $5.10
              </text>

              <text
                x="230"
                y="220"
                className="marketResistanceText"
              >
                Stop raised
              </text>

              <text
                x="425"
                y="180"
                className="marketResistanceText"
              >
                Higher low
              </text>

              <text
                x="610"
                y="135"
                className="marketBreakoutText"
              >
                Trail below structure
              </text>
            </svg>
          </div>

          <div className="marketConfirmationCard">
            <span>TRAILING STOP CHECKLIST</span>

            <div className="marketConfirmationItem">
              <strong>1</strong>

              <div>
                <small>Wait for progress</small>
                <p>
                  Do not trail the stop before the trade develops.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>2</strong>

              <div>
                <small>Use structure</small>
                <p>
                  Trail below higher lows, support, VWAP, or another valid level.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>3</strong>

              <div>
                <small>Respect volatility</small>
                <p>
                  Leave enough room for normal price movement.
                </p>
              </div>
            </div>

            <p className="marketConfirmationNote">
              Never widen the stop simply to avoid accepting the original
              planned loss.
            </p>
          </div>
        </div>
      </div>

      <div className="lessonVisualExplanation">
        <div>
          <span>1</span>
          <p>The original stop protects the initial trade risk.</p>
        </div>

        <div>
          <span>2</span>
          <p>Raise the stop only after price creates stronger structure.</p>
        </div>

        <div>
          <span>3</span>
          <p>Leave enough space for normal volatility and pullbacks.</p>
        </div>
      </div>
    </div>
  );
}

function ProfitTargetsVisual() {
  return (
    <div className="lessonVisualCard">
      <div className="lessonVisualHeader">
        <div>
          <span>PROFIT TARGETS VISUAL</span>
          <h3>Plan where profits will be taken</h3>
        </div>

        <strong className="marketVisualBadge">
          Structure-based targets
        </strong>
      </div>

      <div className="marketVisualBody">
        <div className="marketBalancePanel">
          <div className="marketSide marketBuyerSide">
            <span>TRADE ENTRY</span>

            <strong>$5.20</strong>

            <p>
              The trade begins with a planned entry, stop, and realistic
              profit targets.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Entry</small>
                <strong>$5.20</strong>
                <span>planned execution</span>
              </div>

              <div>
                <small>Stop</small>
                <strong>$5.10</strong>
                <span>$0.10 risk</span>
              </div>

              <div>
                <small>Risk</small>
                <strong>1R</strong>
                <span>defined before entry</span>
              </div>
            </div>
          </div>

          <div className="marketPriceDiscovery">
            <span>PROFIT PLAN</span>

            <strong>T1 + T2 + Runner</strong>

            <div className="marketPressureMeter">
              <div className="marketDemandPressure">
                Lock in gains
              </div>

              <div className="marketSupplyPressure">
                Leave room to continue
              </div>
            </div>

            <p>
              Partial exits can protect gains while allowing part of the
              position to benefit from continued momentum.
            </p>
          </div>

          <div className="marketSide marketSellerSide">
            <span>PLANNED TARGETS</span>

            <strong>Realistic reward</strong>

            <p>
              Targets should reflect resistance, prior highs, volatility,
              ATR, and the remaining reward available.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Target 1</small>
                <strong>$5.40</strong>
                <span>first resistance</span>
              </div>

              <div>
                <small>Target 2</small>
                <strong>$5.60</strong>
                <span>next resistance</span>
              </div>

              <div>
                <small>Runner</small>
                <strong>Trail</strong>
                <span>only if momentum holds</span>
              </div>
            </div>
          </div>
        </div>

        <div className="marketBreakoutPanel">
          <div className="marketBreakoutChart">
            <span className="visualPanelLabel">
              ENTRY, STOP, AND PROFIT TARGETS
            </span>

            <svg
              viewBox="0 0 800 320"
              role="img"
              aria-label="Price moving from entry toward two planned profit targets with a stop below entry"
            >
              <line
                x1="60"
                y1="250"
                x2="740"
                y2="250"
                className="visualAxis"
              />

              <line
                x1="70"
                y1="225"
                x2="730"
                y2="225"
                className="marketResistanceLine"
              />

              <line
                x1="70"
                y1="175"
                x2="730"
                y2="175"
                className="marketResistanceLine"
              />

              <line
                x1="70"
                y1="105"
                x2="730"
                y2="105"
                className="marketResistanceLine"
              />

              <line
                x1="70"
                y1="275"
                x2="730"
                y2="275"
                className="visualAxis"
              />

              <polyline
                points="
                  90,235
                  155,225
                  220,205
                  285,190
                  350,172
                  420,160
                  490,135
                  560,108
                  630,90
                  710,65
                "
                className="marketBreakoutPriceLine"
              />

              <text
                x="85"
                y="215"
                className="marketResistanceText"
              >
                Entry $5.20
              </text>

              <text
                x="85"
                y="295"
                className="marketVolumeText"
              >
                Stop $5.10
              </text>

              <text
                x="560"
                y="165"
                className="marketBreakoutText"
              >
                Target 1: $5.40
              </text>

              <text
                x="555"
                y="95"
                className="marketBreakoutText"
              >
                Target 2: $5.60
              </text>

              <circle
                cx="350"
                cy="172"
                r="9"
                className="marketBreakoutPoint"
              />

              <circle
                cx="560"
                cy="108"
                r="9"
                className="marketBreakoutPoint"
              />
            </svg>
          </div>

          <div className="marketConfirmationCard">
            <span>TARGET CHECKLIST</span>

            <div className="marketConfirmationItem">
              <strong>1</strong>

              <div>
                <small>Market structure</small>
                <p>
                  Use resistance, prior highs, and reaction zones.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>2</strong>

              <div>
                <small>Volatility</small>
                <p>
                  Compare the target with ATR and recent price range.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>3</strong>

              <div>
                <small>Partial profits</small>
                <p>
                  Reduce risk while keeping part of the trade open.
                </p>
              </div>
            </div>

            <p className="marketConfirmationNote">
              A target should be planned before entry and reassessed when
              market conditions weaken.
            </p>
          </div>
        </div>
      </div>

      <div className="lessonVisualExplanation">
        <div>
          <span>1</span>
          <p>Choose targets from real market levels.</p>
        </div>

        <div>
          <span>2</span>
          <p>Confirm the target is realistic for current volatility.</p>
        </div>

        <div>
          <span>3</span>
          <p>Use partial profits to protect gains and manage the runner.</p>
        </div>
      </div>
    </div>
  );
}

function EntryConfirmationVisual() {
  return (
    <div className="lessonVisualCard">
      <div className="lessonVisualHeader">
        <div>
          <span>ENTRY CONFIRMATION VISUAL</span>
          <h3>Wait for evidence before entering</h3>
        </div>

        <strong className="marketVisualBadge">
          Confirmation first
        </strong>
      </div>

      <div className="marketVisualBody">
        <div className="marketBalancePanel">
          <div className="marketSide marketBuyerSide">
            <span>SETUP</span>

            <strong>Worth watching</strong>

            <p>
              A setup creates interest, but it does not yet provide
              enough evidence for an entry.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Resistance</small>
                <strong>$5.20</strong>
                <span>key level</span>
              </div>

              <div>
                <small>Volume</small>
                <strong>Average</strong>
                <span>not confirmed</span>
              </div>

              <div>
                <small>Status</small>
                <strong>Watching</strong>
                <span>no entry yet</span>
              </div>
            </div>
          </div>

          <div className="marketPriceDiscovery">
            <span>CONFIRMATION</span>

            <strong>Price + Volume + Hold</strong>

            <div className="marketPressureMeter">
              <div className="marketDemandPressure">
                Buyers gaining control
              </div>

              <div className="marketSupplyPressure">
                Risk conditions checked
              </div>
            </div>

            <p>
              Confirmation should match the setup and show that the
              planned trade is actually developing.
            </p>
          </div>

          <div className="marketSide marketSellerSide">
            <span>CONFIRMED ENTRY</span>

            <strong>Evidence appears</strong>

            <p>
              Price breaks above resistance, holds the level, and
              volume increases before the trader enters.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Breakout</small>
                <strong>$5.24</strong>
                <span>above resistance</span>
              </div>

              <div>
                <small>Volume</small>
                <strong>Expanding</strong>
                <span>participation rises</span>
              </div>

              <div>
                <small>Status</small>
                <strong>Ready</strong>
                <span>entry considered</span>
              </div>
            </div>
          </div>
        </div>

        <div className="marketBreakoutPanel">
          <div className="marketBreakoutChart">
            <span className="visualPanelLabel">
              BREAKOUT CONFIRMATION
            </span>

            <svg
              viewBox="0 0 800 320"
              role="img"
              aria-label="Price breaks above resistance, holds the level, and volume increases before entry"
            >
              <line
                x1="60"
                y1="250"
                x2="740"
                y2="250"
                className="visualAxis"
              />

              <line
                x1="70"
                y1="175"
                x2="730"
                y2="175"
                className="marketResistanceLine"
              />

              <polyline
                points="
                  80,230
                  150,215
                  220,205
                  290,190
                  360,182
                  430,165
                  500,155
                  570,125
                  650,95
                  720,70
                "
                className="marketBreakoutPriceLine"
              />

              <g className="marketVolumeBars">
                <rect x="110" y="265" width="30" height="20" />
                <rect x="160" y="260" width="30" height="25" />
                <rect x="210" y="255" width="30" height="30" />
                <rect x="260" y="250" width="30" height="35" />
                <rect x="310" y="242" width="30" height="43" />
                <rect x="360" y="230" width="30" height="55" />
                <rect x="410" y="215" width="30" height="70" />
                <rect x="460" y="195" width="30" height="90" />
                <rect x="510" y="170" width="30" height="115" />
                <rect x="560" y="145" width="30" height="140" />
              </g>

              <text
                x="90"
                y="165"
                className="marketResistanceText"
              >
                Resistance $5.20
              </text>

              <text
                x="500"
                y="115"
                className="marketBreakoutText"
              >
                Break and hold
              </text>

              <text
                x="500"
                y="305"
                className="marketVolumeText"
              >
                Volume expands
              </text>
            </svg>
          </div>

          <div className="marketConfirmationCard">
            <span>ENTRY CHECKLIST</span>

            <div className="marketConfirmationItem">
              <strong>1</strong>

              <div>
                <small>Price</small>
                <p>
                  Confirm the level breaks, holds, or rejects as planned.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>2</strong>

              <div>
                <small>Volume and momentum</small>
                <p>
                  Participation should support the direction of the setup.
                </p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>3</strong>

              <div>
                <small>Market conditions</small>
                <p>
                  Check spread, liquidity, VWAP, and nearby levels.
                </p>
              </div>
            </div>

            <p className="marketConfirmationNote">
              Weak or incomplete confirmation means wait or skip the trade.
            </p>
          </div>
        </div>
      </div>

      <div className="lessonVisualExplanation">
        <div>
          <span>1</span>
          <p>A setup creates interest, not permission to enter.</p>
        </div>

        <div>
          <span>2</span>
          <p>Confirmation must match the planned trade idea.</p>
        </div>

        <div>
          <span>3</span>
          <p>Weak conditions should delay or cancel the entry.</p>
        </div>
      </div>
    </div>
  );
}

function UnderstandingMarketVisual() {   
  return (
    <div className="lessonVisualCard">
      <div className="lessonVisualHeader">
        <div>
          <span>MARKET STRUCTURE VISUAL</span>
          <h3>Buyers, sellers, supply, and demand</h3>
        </div>

        <strong className="marketVisualBadge">
          Price discovery
        </strong>
      </div>

      <div className="marketVisualBody">
        <div className="marketBalancePanel">
          <div className="marketSide marketBuyerSide">
            <span>BUYERS</span>
            <strong>Demand</strong>
            <p>
              Buyers compete to purchase shares. Stronger demand
              can push price higher.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Bid</small>
                <strong>$9.98</strong>
                <span>400 shares</span>
              </div>

              <div>
                <small>Bid</small>
                <strong>$9.97</strong>
                <span>700 shares</span>
              </div>

              <div>
                <small>Bid</small>
                <strong>$9.96</strong>
                <span>1,100 shares</span>
              </div>
            </div>
          </div>

          <div className="marketPriceDiscovery">
            <span>LAST PRICE</span>
            <strong>$10.00</strong>

            <div className="marketPressureMeter">
              <div className="marketDemandPressure">
                Demand
              </div>

              <div className="marketSupplyPressure">
                Supply
              </div>
            </div>

            <p>
              Price changes as buyers and sellers compete for
              available shares.
            </p>
          </div>

          <div className="marketSide marketSellerSide">
            <span>SELLERS</span>
            <strong>Supply</strong>
            <p>
              Sellers offer shares to the market. Stronger supply
              can pressure price lower.
            </p>

            <div className="marketOrderStack">
              <div>
                <small>Ask</small>
                <strong>$10.02</strong>
                <span>300 shares</span>
              </div>

              <div>
                <small>Ask</small>
                <strong>$10.03</strong>
                <span>650 shares</span>
              </div>

              <div>
                <small>Ask</small>
                <strong>$10.04</strong>
                <span>900 shares</span>
              </div>
            </div>
          </div>
        </div>

        <div className="marketBreakoutPanel">
          <div className="marketBreakoutChart">
            <span className="visualPanelLabel">
              RESISTANCE BREAKOUT
            </span>

            <svg
              viewBox="0 0 800 320"
              role="img"
              aria-label="Price breaking above resistance with volume confirmation"
            >
              <line
                x1="60"
                y1="245"
                x2="750"
                y2="245"
                className="visualAxis"
              />

              <line
                x1="60"
                y1="135"
                x2="750"
                y2="135"
                className="marketResistanceLine"
              />

              <polyline
                points="
                  70,230
                  145,205
                  220,220
                  295,175
                  365,195
                  435,150
                  500,178
                  565,132
                  630,92
                  710,62
                "
                className="marketBreakoutPriceLine"
              />

              <g className="marketVolumeBars">
                <rect x="90" y="260" width="34" height="28" />
                <rect x="145" y="254" width="34" height="34" />
                <rect x="200" y="250" width="34" height="38" />
                <rect x="255" y="246" width="34" height="42" />
                <rect x="310" y="242" width="34" height="46" />
                <rect x="365" y="238" width="34" height="50" />
                <rect x="420" y="232" width="34" height="56" />
                <rect x="475" y="224" width="34" height="64" />
                <rect x="530" y="205" width="34" height="83" />
                <rect x="585" y="180" width="34" height="108" />
                <rect x="640" y="155" width="34" height="133" />
              </g>

              <text
                x="75"
                y="125"
                className="marketResistanceText"
              >
                Resistance $10.20
              </text>

              <text
                x="575"
                y="82"
                className="marketBreakoutText"
              >
                Breakout
              </text>

              <text
                x="575"
                y="305"
                className="marketVolumeText"
              >
                Volume expands
              </text>
            </svg>
          </div>

          <div className="marketConfirmationCard">
            <span>BREAKOUT CONFIRMATION</span>

            <div className="marketConfirmationItem">
              <strong>1</strong>

              <div>
                <small>Price</small>
                <p>Moves above resistance.</p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>2</strong>

              <div>
                <small>Volume</small>
                <p>Expands as buyers become more aggressive.</p>
              </div>
            </div>

            <div className="marketConfirmationItem">
              <strong>3</strong>

              <div>
                <small>Hold</small>
                <p>Price remains above the breakout level.</p>
              </div>
            </div>

            <p className="marketConfirmationNote">
              A breakout without volume or follow-through may be
              more vulnerable to failure.
            </p>
          </div>
        </div>
      </div>

      <div className="lessonVisualExplanation">
        <div>
          <span>1</span>
          <p>Demand represents buyers competing for shares.</p>
        </div>

        <div>
          <span>2</span>
          <p>Supply represents sellers offering shares.</p>
        </div>

        <div>
          <span>3</span>
          <p>Volume helps confirm whether a breakout has support.</p>
        </div>
      </div>
    </div>
  );
}

function LessonVisual({ lessonId }) {
if (lessonId === "float-share-supply") {
  return <FloatShareSupplyVisual />;
}

if (lessonId === "volatility-price-range") {
  return <VolatilityPriceRangeVisual />;
}

if (lessonId === "average-true-range") {
  return <AverageTrueRangeVisual />;
}

if (lessonId === "risk-reward-ratio") {
  return <RiskRewardRatioVisual />;
}

if (lessonId === "position-sizing") {
  return <PositionSizingVisual />;
}

if (lessonId === "trade-planning") {
  return <TradePlanningVisual />;
}

if (lessonId === "entry-confirmation") {
  return <EntryConfirmationVisual />;
}

if (lessonId === "profit-targets") {
  return <ProfitTargetsVisual />;
}

if (lessonId === "trailing-stops") {
  return <TrailingStopsVisual />;
}

if (lessonId === "breakouts-false-breakouts") {
  return <BreakoutsFalseBreakoutsVisual />;
}

if (lessonId === "vwap-basics") {
  return <VwapBasicsVisual />;
}

if (lessonId === "level-2-basics") {
  return <Level2BasicsVisual />;
}

if (lessonId === "trading-psychology-discipline") {
  return <TradingPsychologyDisciplineVisual />;
}

  if (lessonId === "understanding-market") {
    return <UnderstandingMarketVisual />;
  }

  if (lessonId === "how-stocks-move") {
    return <HowStocksMoveVisual />;
  }

  if (lessonId === "market-orders") {
    return <MarketOrdersVisual />;
  }

 if (lessonId === "bid-ask-spread") {
  return <BidAskSpreadVisual />;
} 

  if (lessonId === "volume-liquidity") {
  return <VolumeLiquidityVisual />;
}

if (lessonId === "relative-volume") {
  return <RelativeVolumeVisual />;
}

  if (lessonId === "candlestick-basics") {
    return <CandlestickBasicsVisual />;
  }

  if (lessonId === "support-resistance") {
    return <SupportResistanceVisual />;
  }

  if (lessonId === "macd-basics") {
    return <MacdDivergenceVisual />;
  }

  if (lessonId === "stop-loss") {
    return <StopLossVisual />;
  }

  if (lessonId === "short-selling") {
    return <ShortSellingVisual />;
  }

  return null;
}

export default LessonVisual;